const THEME_STORAGE_KEY = 'backoffice-ui-theme'
export const THEME_LIGHT = 'light'
export const THEME_DARK = 'dark'
const RUNTIME_DARK_STYLE_ID = 'backoffice-runtime-dark-style'
const GLOBAL_PLACEHOLDER_STYLE_ID = 'backoffice-global-placeholder-style'
const RUNTIME_DARK_INPUT_MATCHER =
  'input,textarea,select,.MuiInputBase-root,.MuiInputBase-input,.MuiFilledInput-root,.MuiOutlinedInput-root,.autoCompleteInput,.autoCompleteLazyLoadinputDiv'

let runtimeDarkObserver
let runtimeDarkPatchTimer

// Injects a global ::placeholder rule so every input/textarea in the entire
// application uses var(--placeHolderColor). The token is updated per-theme
// inside applyTheme(), making this the single control point for placeholder colour.
const ensureGlobalPlaceholderStyle = () => {
  if (typeof document === 'undefined') return
  if (document.getElementById(GLOBAL_PLACEHOLDER_STYLE_ID)) return

  const style = document.createElement('style')
  style.id = GLOBAL_PLACEHOLDER_STYLE_ID
  style.innerHTML = `
    *::placeholder,
    input::placeholder,
    textarea::placeholder,
    .MuiInputBase-input::placeholder,
    .MuiInput-input::placeholder,
    .autoCompleteInput::placeholder {
      color: var(--placeHolderColor) !important;
      -webkit-text-fill-color: var(--placeHolderColor) !important;
      opacity: 1 !important;
    }
  `
  document.head.appendChild(style)
}

const isLightRgbColor = (rgbColor = '') => {
  const match = rgbColor.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i
  )
  if (!match) return false

  const red = Number(match[1])
  const green = Number(match[2])
  const blue = Number(match[3])
  const alpha = match[4] === undefined ? 1 : Number(match[4])

  if (alpha < 0.85) return false

  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue
  return luminance > 170
}

// Detects dark/black text colours that become invisible on dark backgrounds.
const isDarkRgbColor = (rgbColor = '') => {
  const match = rgbColor.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i
  )
  if (!match) return false

  const red = Number(match[1])
  const green = Number(match[2])
  const blue = Number(match[3])
  const alpha = match[4] === undefined ? 1 : Number(match[4])

  // Ignore fully/near-transparent text — inherits from parent, not hardcoded.
  if (alpha < 0.5) return false

  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue
  // Luminance < 80 covers black (#000), near-black (#262D33 ≈ 44), very dark greys.
  return luminance < 80
}

const ensureRuntimeDarkStyle = () => {
  if (typeof document === 'undefined') return
  if (document.getElementById(RUNTIME_DARK_STYLE_ID)) return

  const style = document.createElement('style')
  style.id = RUNTIME_DARK_STYLE_ID
  style.innerHTML = `
    .MuiDataGrid-columnHeaderTitle,
    .MuiDataGrid-columnHeaderTitleContainer,
    .MuiDataGrid-columnHeaderTitleContainerContent,
    .MuiDataGrid-columnHeaderDraggableContainer {
      color: var(--airagridHeaderColor) !important;
      -webkit-text-fill-color: var(--airagridHeaderColor) !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    .ag-header-cell,
    .ag-header-cell-label,
    .ag-header-cell-text {
      color: var(--airagridHeaderColor) !important;
      -webkit-text-fill-color: var(--airagridHeaderColor) !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    body[data-theme='dark'] .ag-theme-alpine {
      --ag-background-color: #262d33;
      --ag-foreground-color: var(--dark-text-primary);
      --ag-header-background-color: #262d33;
      --ag-header-foreground-color: var(--dark-text-primary);
      --ag-modal-overlay-background-color: rgba(38, 45, 51, 0.72);
      --ag-odd-row-background-color: #31393f;
      --ag-row-hover-color: var(--dark-panel-hover);
      --ag-border-color: var(--dark-panel-border);
      --ag-secondary-border-color: var(--dark-panel-border);
      --ag-input-focus-border-color: var(--dark-search-action-bg);
      --ag-alpine-active-color: var(--dark-search-action-bg);
      --ag-selected-row-background-color: #374351;
      --ag-range-selection-border-color: var(--dark-search-action-bg);
      --ag-font-family: var(--default-FontName);
      --ag-font-size: var(--default-FontSize);
    }

    body[data-theme='dark'] .ag-theme-alpine,
    body[data-theme='dark'] .ag-root-wrapper,
    body[data-theme='dark'] .ag-root,
    body[data-theme='dark'] .ag-header,
    body[data-theme='dark'] .ag-header-viewport,
    body[data-theme='dark'] .ag-header-row,
    body[data-theme='dark'] .ag-row,
    body[data-theme='dark'] .ag-row-even,
    body[data-theme='dark'] .ag-cell,
    body[data-theme='dark'] .ag-pinned-left-cols-container,
    body[data-theme='dark'] .ag-center-cols-container,
    body[data-theme='dark'] .ag-floating-top,
    body[data-theme='dark'] .ag-floating-bottom,
    body[data-theme='dark'] .ag-body-viewport,
    body[data-theme='dark'] .ag-center-cols-viewport,
    body[data-theme='dark'] .ag-paging-panel,
    body[data-theme='dark'] .ag-status-bar {
      background-color: #262d33 !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .ag-row-odd {
      background-color: #31393f !important;
    }

    body[data-theme='dark'] .ag-row-odd .ag-cell {
      background-color: #31393f !important;
    }

    body[data-theme='dark'] .ag-header-cell,
    body[data-theme='dark'] .ag-header-cell-label,
    body[data-theme='dark'] .ag-header-cell-text,
    body[data-theme='dark'] .ag-paging-description,
    body[data-theme='dark'] .ag-paging-page-summary-panel,
    body[data-theme='dark'] .ag-paging-row-summary-panel,
    body[data-theme='dark'] .ag-cell-value {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .ag-overlay-loading-center,
    body[data-theme='dark'] .ag-overlay-no-rows-center,
    body[data-theme='dark'] .custom-loading-overlay {
      background-color: var(--dark-panel-surface-elevated) !important;
      color: var(--dark-text-primary) !important;
      border: 1px solid var(--dark-panel-border) !important;
      border-radius: 6px !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-root,
    body[data-theme='dark'] .MuiDataGrid-main,
    body[data-theme='dark'] .MuiDataGrid-columnHeaders,
    body[data-theme='dark'] .MuiDataGrid-footerContainer,
    body[data-theme='dark'] .MuiDataGrid-overlay,
    body[data-theme='dark'] .MuiDataGrid-overlayWrapper,
    body[data-theme='dark'] .MuiDataGrid-overlayWrapperInner,
    body[data-theme='dark'] .gridData .toolbarContainer {
      background-color: var(--dataGridSurfaceColor) !important;
      color: var(--dataGridTextColor) !important;
      border-color: var(--dataGridBorderColor) !important;
    }

    body[data-theme='dark'] .MuiPaper-root,
    body[data-theme='dark'] .MuiDialog-paper,
    body[data-theme='dark'] .MuiPopover-paper,
    body[data-theme='dark'] .MuiMenu-paper,
    body[data-theme='dark'] .MuiMenu-list,
    body[data-theme='dark'] .MuiAutocomplete-popper .MuiPaper-root,
    body[data-theme='dark'] .MuiDataGrid-paper,
    body[data-theme='dark'] .MuiDataGrid-panel,
    body[data-theme='dark'] .MuiDataGrid-panelWrapper,
    body[data-theme='dark'] .MuiDataGrid-menu,
    body[data-theme='dark'] .MuiDataGrid-columnsManagement,
    body[data-theme='dark'] .MuiDataGrid-columnsManagementHeader,
    body[data-theme='dark'] .MuiDataGrid-columnsManagementFooter,
    body[data-theme='dark'] .MuiDataGrid-panelContent {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnsManagement .MuiFormControlLabel-label,
    body[data-theme='dark'] .MuiDataGrid-columnsManagement .MuiTypography-root,
    body[data-theme='dark'] .MuiDataGrid-columnsManagement .MuiInputBase-input,
    body[data-theme='dark'] .MuiDataGrid-columnsManagement .MuiSvgIcon-root {
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      stroke: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .MuiAutocomplete-popper .MuiPaper-root,
    body[data-theme='dark'] .MuiPopover-paper,
    body[data-theme='dark'] .MuiMenu-paper {
      border: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .MuiPopover-root.airaList,
    body[data-theme='dark'] .MuiPopover-root.airaList .MuiPaper-root,
    body[data-theme='dark'] .MuiPopover-root.airaList .MuiMenu-paper,
    body[data-theme='dark'] .MuiPopover-root.airaList .MuiMenu-list {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-cell[data-field='actions'],
    body[data-theme='dark'] .MuiDataGrid-pinnedColumns--right .MuiDataGrid-cell,
    body[data-theme='dark'] .MuiDataGrid-pinnedColumnHeaders--right,
    body[data-theme='dark'] .MuiDataGrid-pinnedColumnHeaders {
      background-color: var(--dataGridSurfaceColor) !important;
      color: var(--dataGridTextColor) !important;
      border-color: var(--dataGridBorderColor) !important;
    }

    body[data-theme='dark'] .MuiMenuItem-root,
    body[data-theme='dark'] .MuiListItemText-primary,
    body[data-theme='dark'] .MuiDialogTitle-root,
    body[data-theme='dark'] .MuiDialogContent-root,
    body[data-theme='dark'] .MuiDialogActions-root,
    body[data-theme='dark'] .MuiInputLabel-root,
    body[data-theme='dark'] .MuiFormLabel-root,
    body[data-theme='dark'] [class$='InputLabel'],
    body[data-theme='dark'] [class*='InputLabel '],
    body[data-theme='dark'] .MuiSelect-select,
    body[data-theme='dark'] .MuiAutocomplete-option {
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .dropdown .MuiSelect-icon,
    body[data-theme='dark'] .dropdown .MuiNativeSelect-icon,
    body[data-theme='dark'] .MuiSelect-icon,
    body[data-theme='dark'] .MuiNativeSelect-icon {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      stroke: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
      opacity: 1 !important;
    }

    body[data-theme='dark'] .morevert-icon,
    body[data-theme='dark'] .morevert-icon .MuiSvgIcon-root,
    body[data-theme='dark'] .moreverticonmenuitem,
    body[data-theme='dark'] .moreverticonmenuitem .MuiTypography-root,
    body[data-theme='dark'] .moreverticonmenuitem .moreoverText {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .moreverticonmenuitem img,
    body[data-theme='dark'] .moreverticonmenuitem .moreoverImaget {
      filter: brightness(0) invert(1) !important;
    }

    body[data-theme='dark'] .moreverticonmenuitem svg,
    body[data-theme='dark'] .moreverticonmenuitem .injected-svg,
    body[data-theme='dark'] .moreverticonmenuitem svg path,
    body[data-theme='dark'] .moreverticonmenuitem .injected-svg path,
    body[data-theme='dark'] .morevert-icon svg,
    body[data-theme='dark'] .morevert-icon svg path {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      stroke: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .MuiMenuItem-root.Mui-focusVisible,
    body[data-theme='dark'] .MuiMenuItem-root:hover,
    body[data-theme='dark'] .MuiAutocomplete-option[aria-selected='true'],
    body[data-theme='dark'] .MuiAutocomplete-option.Mui-focused {
      background-color: var(--dark-panel-hover) !important;
    }

    body[data-theme='dark'] .dropdown .MuiMenuItem-root:hover,
    body[data-theme='dark'] .MuiMenu-list .MuiMenuItem-root:hover,
    body[data-theme='dark'] .MuiAutocomplete-listbox .MuiAutocomplete-option:hover,
    body[data-theme='dark'] .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused,
    body[data-theme='dark'] .ui.search > .results .result:hover,
    body[data-theme='dark'] .ui.category.search > .results .category .result:hover {
      background-color: var(--dark-panel-hover) !important;
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .autoCompleteLazyLoadPopper {
      background-color: var(--dark-panel-surface) !important;
      border: 1px solid var(--dark-panel-border) !important;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
    }

    body[data-theme='dark'] .autoCompleteLazyLoadPopper .infiniteScrollDiv,
    body[data-theme='dark'] .autoCompleteLazyLoadPopper .optionLabel {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .autoCompleteLazyLoadPopper .optionLabel:hover,
    body[data-theme='dark'] .autoCompleteLazyLoadPopper .optionLabel.selected {
      background-color: var(--dark-panel-hover) !important;
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .autoCompleteLazyLoadPopper .optionLabel.disableLabel {
      color: var(--dark-text-tertiary) !important;
      -webkit-text-fill-color: var(--dark-text-tertiary) !important;
    }

    body[data-theme='dark'] .ag-theme-alpine .ag-row:hover,
    body[data-theme='dark'] .ag-theme-alpine .ag-row-hover,
    body[data-theme='dark'] .ag-theme-alpine .ag-row:hover .ag-cell,
    body[data-theme='dark'] .ag-theme-alpine .ag-row-hover .ag-cell,
    body[data-theme='dark'] .ag-root .ag-row:hover,
    body[data-theme='dark'] .ag-root .ag-row-hover,
    body[data-theme='dark'] .ag-root .ag-row:hover .ag-cell,
    body[data-theme='dark'] .ag-root .ag-row-hover .ag-cell,
    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-row:hover,
    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-row:hover .MuiDataGrid-cell {
      background-color: var(--dark-panel-hover) !important;
    }

    body[data-theme='dark'] .MuiInputBase-root,
    body[data-theme='dark'] .MuiInput-root,
    body[data-theme='dark'] .MuiInput-underline,
    body[data-theme='dark'] .MuiOutlinedInput-root,
    body[data-theme='dark'] .MuiFilledInput-root,
    body[data-theme='dark'] .MuiInputBase-input,
    body[data-theme='dark'] .MuiInputBase-inputMultiline,
    body[data-theme='dark'] .MuiInput-input,
    body[data-theme='dark'] .MuiOutlinedInput-inputMultiline,
    body[data-theme='dark'] .MuiFilledInput-inputMultiline,
    body[data-theme='dark'] .autoCompleteLazyLoadinputDiv,
    body[data-theme='dark'] .autoCompleteInput,
    body[data-theme='dark'] .autoCompleteInput_form,
    body[data-theme='dark'] .TextFieldParent .MuiInputBase-root,
    body[data-theme='dark'] .TextFieldParent input,
    body[data-theme='dark'] .TextFieldParent textarea,
    body[data-theme='dark'] textarea.MuiInputBase-input {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-search-text) !important;
      border-color: var(--dark-search-border) !important;
      -webkit-text-fill-color: var(--dark-search-text) !important;
    }

    body[data-theme='dark'] .MuiInputBase-root,
    body[data-theme='dark'] .MuiOutlinedInput-root,
    body[data-theme='dark'] .MuiFilledInput-root,
    body[data-theme='dark'] .MuiAutocomplete-root .MuiInputBase-root,
    body[data-theme='dark'] .MuiFormControl-root .MuiInputBase-root {
      // box-shadow: inset 0 0 0 1px var(--dark-search-border) !important;
      border-radius: 4px;
      border: 1px solid var(--dark-search-border) !important;
    }

    body[data-theme='dark'] .autoCompleteLazyLoadinputDiv {
      border: 1px solid var(--dark-search-border) !important;
      border-radius: 4px;
    }

    body[data-theme='dark'] .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline,
    body[data-theme='dark'] .MuiAutocomplete-root .MuiOutlinedInput-notchedOutline,
    body[data-theme='dark'] .MuiFormControl-root .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }

    body[data-theme='dark'] .MuiAutocomplete-root {
      border: none !important;
    }

    body[data-theme='dark'] .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
    body[data-theme='dark'] .MuiAutocomplete-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
    body[data-theme='dark'] .MuiAutocomplete-root .Mui-focused .MuiOutlinedInput-notchedOutline,
    body[data-theme='dark'] .MuiInputBase-root.Mui-focused {
      border-color: var(--dark-search-action-bg) !important;
    }

    body[data-theme='dark'] .textbox .outlineError .MuiInputBase-root,
    body[data-theme='dark'] .MuiFormControl-root.outlineError .MuiInputBase-root,
    body[data-theme='dark'] .MuiInputBase-root.Mui-error,
    body[data-theme='dark'] .MuiOutlinedInput-root.Mui-error,
    body[data-theme='dark'] .MuiFilledInput-root.Mui-error {
      border: 1px solid var(--required-color) !important;
      border-color: var(--required-color) !important;
    }

    body[data-theme='dark'] .textbox .outlineError .MuiInputBase-root.Mui-focused,
    body[data-theme='dark'] .MuiFormControl-root.outlineError .MuiInputBase-root.Mui-focused,
    body[data-theme='dark'] .MuiInputBase-root.Mui-error.Mui-focused,
    body[data-theme='dark'] .MuiOutlinedInput-root.Mui-error.Mui-focused,
    body[data-theme='dark'] .MuiFilledInput-root.Mui-error.Mui-focused {
      border-color: var(--required-color) !important;
    }

    body[data-theme='dark'] .MuiInput-underline:before,
    body[data-theme='dark'] .MuiFilledInput-underline:before,
    body[data-theme='dark'] .MuiInput-underline:after,
    body[data-theme='dark'] .MuiFilledInput-underline:after {
      border-bottom-color: var(--dark-search-border) !important;
    }

    body[data-theme='dark'] input:not(.MuiInputBase-input),
    body[data-theme='dark'] textarea:not(.MuiInputBase-input),
    body[data-theme='dark'] select,
    body[data-theme='dark'] .autoCompleteInput,
    body[data-theme='dark'] .autoCompleteInput_form,
    body[data-theme='dark'] .MuiSelect-select {
      // box-shadow: inset 0 0 0 1px var(--dark-search-border) !important;
      border-color: 1px solid var(--dark-search-border) !important;
    }

    body[data-theme='dark'] .multilinetextbox textarea,
    body[data-theme='dark'] .photoGuideTextArea .multilinetextbox textarea {
      border: 1px solid var(--dark-search-border) !important;
      border-radius: 4px !important;
    }

    body[data-theme='dark'] .MuiInputBase-input::placeholder,
    body[data-theme='dark'] .MuiInputBase-inputMultiline::placeholder,
    body[data-theme='dark'] .MuiInput-input::placeholder,
    body[data-theme='dark'] textarea::placeholder,
    body[data-theme='dark'] .autoCompleteInput::placeholder {
      color: var(--dark-search-placeholder) !important;
      -webkit-text-fill-color: var(--dark-search-placeholder) !important;
      opacity: 1 !important;
    }

    body[data-theme='dark'] .MuiSelect-select[data-value=''],
    body[data-theme='dark'] .MuiSelect-select:not([data-value]),
    body[data-theme='dark'] .MuiNativeSelect-select option[value=''] {
      color: var(--dark-search-placeholder) !important;
      -webkit-text-fill-color: var(--dark-search-placeholder) !important;
    }

    body[data-theme='dark'] .mandatorySymbol,
    body[data-theme='dark'] .mandatory-symbol,
    body[data-theme='dark'] .required-asterisk,
    body[data-theme='dark'] .Required-color,
    body[data-theme='dark'] .requiredClass,
    body[data-theme='dark'] .required,
    body[data-theme='dark'] .validation-icon-error,
    body[data-theme='dark'] .validation-icon-error path, body[data-theme='dark'] .add-non-working-days-dialog__mandatory-symbol, body[data-theme='dark'] .fault-status-update-dialog .fault-status-update-dialog__mandatory {
      color: var(--mandatorySymbol) !important;
      -webkit-text-fill-color: var(--mandatorySymbol) !important;
    }

    body[data-theme='dark'] .textbox .MuiInputBase-input {
      padding: 0px !important;
    }

    body[data-theme='dark'] .dropzonearea .label-name,
    body[data-theme='dark'] .signature-container .label-name,
    body[data-theme='dark'] .dropzonearea .customContent,
    body[data-theme='dark'] .signature-container .customContent,
    body[data-theme='dark'] .dropzonearea .dropDown_styles,
    body[data-theme='dark'] .signature-container .dropDown_styles,
    body[data-theme='dark'] .dropzonearea .dropDown_styles_DropFiles,
    body[data-theme='dark'] .signature-container .dropDown_styles_DropFiles,
    body[data-theme='dark'] .dropzonearea .dropDown_styles_Icon,
    body[data-theme='dark'] .signature-container .dropDown_styles_Icon {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
      border: none !important;
    }

    // body[data-theme='dark'] .subMenuitemstyle.active {
    //   border-left: 3px solid var(--dark-search-action-bg) !important;
    // }

    body[data-theme='dark'] .subMenuitemstyle.active .MuiTypography-root {
      color: var(--dark-search-action-bg) !important;
      font-weight: 600 !important;
      font-size: 16px !important;
    }

    body[data-theme='dark'] .drawer .MuiListItemIcon-root img,
    body[data-theme='dark'] .drawer .MuiListItemIcon-root svg,
    body[data-theme='dark'] .drawer .MuiListItemIcon-root .MuiSvgIcon-root,
    body[data-theme='dark'] .drawer .Menuitemstyle img,
    body[data-theme='dark'] .drawer .Menuitemstyle svg,
    body[data-theme='dark'] .drawer [class*='arrow'] img,
    body[data-theme='dark'] .drawer [class*='arrow'] svg,
    body[data-theme='dark'] .drawer [class*='arrow'] .MuiSvgIcon-root {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      stroke: var(--dark-text-primary) !important;
      filter: brightness(0) invert(1) !important;
    }

    body[data-theme='dark'] .expandLessIcon,
    body[data-theme='dark'] [data-testid='KeyboardDoubleArrowLeftIcon'],
    body[data-theme='dark'] [data-testid='ArrowForwardIosSharpIcon'],
    body[data-theme='dark'] .expandLessIcon path,
    body[data-theme='dark'] [data-testid='KeyboardDoubleArrowLeftIcon'] path,
    body[data-theme='dark'] [data-testid='ArrowForwardIosSharpIcon'] path {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      stroke: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .tabControl .tabHeader,
    body[data-theme='dark'] .tabControl .tabHeader .tabHeaderTabs,
    body[data-theme='dark'] .tabControl .tabHeader .MuiTabs-list,
    body[data-theme='dark'] .tabControl .tabHeader .MuiTabs-flexContainer,
    body[data-theme='dark'] .tabControl .tabHeader [role='tablist'] {
      background-color: #262d33 !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .workspace-page .tabControl .tabHeader .MuiTabs-list,
    body[data-theme='dark'] .workspace-page .tabControl .tabHeader .MuiTabs-flexContainer,
    body[data-theme='dark'] .workspace-page .tabControl .tabHeader [role='tablist'] {
      background-color: #31393f !important;
    }

    body[data-theme='dark'] .body-data-page,
    // body[data-theme='dark'] .workspace-page,
    body[data-theme='dark'] .homepagemainsection,
    body[data-theme='dark'] .bodypage {
      background-color: #262d33 !important;
    }

    body[data-theme='dark'] .bodypage:not(:has(.workspace-page)) {
      background-color: #31393f !important;
    }

    body[data-theme='dark'] .drawer,
    body[data-theme='dark'] .drawer .MuiDrawer-paper,
    body[data-theme='dark'] .drawer .MuiPaper-root,
    body[data-theme='dark'] .drawer .drawer__mainmenu__navgroup,
    body[data-theme='dark'] .drawer .menu,
    body[data-theme='dark'] .drawer .levelOne,
    body[data-theme='dark'] .drawer .levelTwo,
    body[data-theme='dark'] .drawer .MuiCollapse-root,
    body[data-theme='dark'] .drawer .MuiCollapse-wrapper,
    body[data-theme='dark'] .drawer .MuiCollapse-wrapperInner {
      background-color: var(--dark-panel-bg) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .drawer .MuiListItem-root,
    body[data-theme='dark'] .drawer .Menuitemstyle,
    body[data-theme='dark'] .drawer .drawer__submenu_item {
      background-color: transparent !important;
    }

    body[data-theme='dark'] .homepagemainsection .homepagecardsection .MuiCard-root,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiAccordionDetails-root
      .MuiCard-root,
    body[data-theme='dark'] .homepagemainsection .widgetsFavorite .MuiCard-root,
    body[data-theme='dark']
      .homepagemainsection
      .widgetsFavorite.favoriteSection
      .MuiCard-root {
      background-color: #262d33 !important;
      border: 1px solid var(--dark-panel-border) !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22) !important;
      border-radius: 20px !important;
    }

    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiCardHeader-avatar
      .MuiAvatar-root,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiAvatar-root.MuiAvatar-circular.MuiAvatar-colorDefault.cursorpointer {
      background-color: var(--cardavatarbgcolor) !important;
    }

    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiCardHeader-root,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiCardContent-root,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .cardcontentsection {
      background-color: #262d33 !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiCardHeader-root
      .MuiCardHeader-title,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .accordionTitle,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .carddatasection
      .carddatasectiondata {
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiCardHeader-root
      .MuiCardHeader-subheader,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .carddatasection
      .carddatasectiontitle,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .carddatasection
      .carddatasectiontitle .text,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .carddatasection
      .carddatasectiontitle .title {
      color: var(--dark-text-secondary) !important;
      -webkit-text-fill-color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .carddatasection.livecases,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .carddatasection.workflow {
      border-right-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeaderTitle,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderTitleContainer,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderTitleContainerContent,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderDraggableContainer,
    body[data-theme='dark'] .MuiDataGrid-cell,
    body[data-theme='dark'] .MuiTablePagination-root,
    body[data-theme='dark'] .MuiTablePagination-selectLabel,
    body[data-theme='dark'] .MuiTablePagination-displayedRows,
    body[data-theme='dark'] .MuiTablePagination-selectIcon,
    body[data-theme='dark'] .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root,
    body[data-theme='dark'] .MuiDataGrid-menuIcon .MuiSvgIcon-root {
      color: var(--dataGridTextColor) !important;
      opacity: 1 !important;
    }

    body[data-theme='dark']
      .MuiDataGrid-columnHeaders
      .MuiDataGrid-columnHeaderTitle,
    body[data-theme='dark']
      .MuiDataGrid-columnHeaders
      .MuiDataGrid-columnHeaderTitleContainer,
    body[data-theme='dark']
      .MuiDataGrid-columnHeaders
      .MuiDataGrid-columnHeaderTitleContainerContent,
    body[data-theme='dark']
      .MuiDataGrid-columnHeaders
      .MuiDataGrid-columnHeaderDraggableContainer {
      color: var(--dataGridTextColor) !important;
      -webkit-text-fill-color: var(--dataGridTextColor) !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeaders .MuiDataGrid-columnSeparator {
      color: var(--dataGridBorderColor) !important;
      opacity: 0.7 !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeader {
      border-color: var(--dataGridBorderColor) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeaders,
    body[data-theme='dark'] .MuiDataGrid-columnHeader,
    body[data-theme='dark'] .MuiDataGrid-columnHeadersInner,
    body[data-theme='dark'] .MuiDataGrid-columnHeadersInner--scrollable,
    body[data-theme='dark'] .MuiDataGrid-pinnedColumns,
    body[data-theme='dark'] .MuiDataGrid-pinnedColumnHeaders,
    body[data-theme='dark'] .MuiDataGrid-pinnedColumns--left,
    body[data-theme='dark'] .MuiDataGrid-pinnedColumns--right {
      background-color: var(--dataGridSurfaceColor) !important;
      color: var(--dataGridTextColor) !important;
      border-color: var(--dataGridBorderColor) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeaders {
      position: relative !important;
      z-index: 3 !important;
    }

    body[data-theme='dark'] .MuiDataGrid-overlay,
    body[data-theme='dark'] .MuiDataGrid-overlayWrapper,
    body[data-theme='dark'] .MuiDataGrid-overlayWrapperInner {
      position: absolute !important;
      top: 56px !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      z-index: 1 !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeadersInner,
    body[data-theme='dark'] .MuiDataGrid-columnHeadersInner--scrollable,
    body[data-theme='dark'] .MuiDataGrid-columnHeader {
      opacity: 1 !important;
      visibility: visible !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeaderTitle,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderTitleContainer,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderTitleContainerContent,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderDraggableContainer {
      color: var(--dataGridTextColor) !important;
      -webkit-text-fill-color: var(--dataGridTextColor) !important;
      text-shadow: 0 0 0 var(--dataGridTextColor) !important;
      font-size: 14px !important;
      line-height: 1.2 !important;
      font-weight: 600 !important;
      opacity: 1 !important;
      visibility: visible !important;
      position: relative !important;
      z-index: 4 !important;
      mix-blend-mode: normal !important;
    }

    body[data-theme='dark'] .ag-header,
    body[data-theme='dark'] .ag-header-viewport,
    body[data-theme='dark'] .ag-header-row,
    body[data-theme='dark'] .ag-pinned-left-header,
    body[data-theme='dark'] .ag-pinned-right-header,
    body[data-theme='dark'] .ag-header-cell,
    body[data-theme='dark'] .ag-header-cell-label,
    body[data-theme='dark'] .ag-header-cell-text {
      background-color: var(--dataGridSurfaceColor) !important;
      color: var(--dataGridTextColor) !important;
      border-color: var(--dataGridBorderColor) !important;
      -webkit-text-fill-color: var(--dataGridTextColor) !important;
      opacity: 1 !important;
      visibility: visible !important;
      text-shadow: 0 0 0 var(--dataGridTextColor) !important;
      font-size: 14px !important;
      line-height: 1.2 !important;
      font-weight: 600 !important;
    }

    body[data-theme='dark'] .ag-root .injected-svg,
    body[data-theme='dark'] .ag-root .injected-svg path,
    body[data-theme='dark'] .ag-root svg.injected-svg,
    body[data-theme='dark'] .ag-root svg.injected-svg path {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      stroke: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .ag-root .injected-svg path[fill='#333333'],
    body[data-theme='dark'] .ag-root .injected-svg path[fill='#000000'],
    body[data-theme='dark'] .ag-root .injected-svg path[fill='black'] {
      fill: var(--dark-text-primary) !important;
      stroke: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root,
    body[data-theme='dark'] .MuiDataGrid-cellCheckbox .MuiCheckbox-root,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderCheckbox .MuiSvgIcon-root,
    body[data-theme='dark'] .MuiDataGrid-cellCheckbox .MuiSvgIcon-root {
      color: var(--dark-text-secondary) !important;
      fill: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-columnHeaderCheckbox .Mui-checked,
    body[data-theme='dark'] .MuiDataGrid-cellCheckbox .Mui-checked,
    body[data-theme='dark'] .MuiDataGrid-columnHeaderCheckbox .Mui-checked .MuiSvgIcon-root,
    body[data-theme='dark'] .MuiDataGrid-cellCheckbox .Mui-checked .MuiSvgIcon-root {
      color: var(--dark-search-action-bg) !important;
      fill: var(--dark-search-action-bg) !important;
    }

    body[data-theme='dark'] .MuiTablePagination-select {
      color: var(--dataGridSelectTextColor) !important;
      background-color: var(--dataGridSelectBgColor) !important;
      border: 1px solid var(--dataGridSelectBorderColor) !important;
      border-radius: 6px;
    }

    body[data-theme='dark'] .MuiDataGrid-scrollbar--horizontal,
    body[data-theme='dark'] .MuiDataGrid-scrollbarFiller,
    body[data-theme='dark'] .MuiDataGrid-filler,
    body[data-theme='dark'] .MuiDataGrid-scrollShadow {
      border-color: var(--dataGridBorderColor) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar {
      height: 8px;
    }

    body[data-theme='dark'] .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-track {
      background: var(--appcontent-bg-color) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb {
      background: var(--scrollBarBgColor) !important;
      border: 1px solid var(--scrollBarBorderColor) !important;
      border-radius: 8px;
    }

    body[data-theme='dark'] .body-data-page,
    body[data-theme='dark'] .stepper-content-section,
    body[data-theme='dark'] .MuiDataGrid-virtualScroller,
    body[data-theme='dark'] .slide-pane__content,
    body[data-theme='dark'] .autoCompleteLazyLoadPopper .infiniteScrollDiv {
      scrollbar-color: var(--scrollBarBgColor) var(--appcontent-bg-color);
      scrollbar-width: thin;
    }

    body[data-theme='dark'] .body-data-page::-webkit-scrollbar,
    body[data-theme='dark'] .stepper-content-section::-webkit-scrollbar,
    body[data-theme='dark'] .MuiDataGrid-virtualScroller::-webkit-scrollbar,
    body[data-theme='dark'] .slide-pane__content::-webkit-scrollbar,
    body[data-theme='dark']
      .autoCompleteLazyLoadPopper
      .infiniteScrollDiv::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    body[data-theme='dark'] .body-data-page::-webkit-scrollbar-track,
    body[data-theme='dark'] .stepper-content-section::-webkit-scrollbar-track,
    body[data-theme='dark']
      .MuiDataGrid-virtualScroller::-webkit-scrollbar-track,
    body[data-theme='dark'] .slide-pane__content::-webkit-scrollbar-track,
    body[data-theme='dark']
      .autoCompleteLazyLoadPopper
      .infiniteScrollDiv::-webkit-scrollbar-track {
      background: var(--appcontent-bg-color);
    }

    body[data-theme='dark'] .body-data-page::-webkit-scrollbar-thumb,
    body[data-theme='dark'] .stepper-content-section::-webkit-scrollbar-thumb,
    body[data-theme='dark']
      .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb,
    body[data-theme='dark'] .slide-pane__content::-webkit-scrollbar-thumb,
    body[data-theme='dark']
      .autoCompleteLazyLoadPopper
      .infiniteScrollDiv::-webkit-scrollbar-thumb {
      background: var(--scrollBarBgColor);
      border: 1px solid var(--scrollBarBorderColor);
      border-radius: 8px;
    }

    body[data-theme='dark'] .MuiInputBase-root.Mui-disabled,
    body[data-theme='dark'] .MuiOutlinedInput-root.Mui-disabled,
    body[data-theme='dark'] .MuiFilledInput-root.Mui-disabled,
    body[data-theme='dark'] .MuiInputBase-input.Mui-disabled,
    body[data-theme='dark'] .autoCompleteLazyLoadinputDiv.disabled,
    body[data-theme='dark'] .autoCompleteLazyLoadinputDiv.disabled .autoCompleteInput,
    body[data-theme='dark'] .autoCompleteLazyLoadinputDiv.disabled .autoCompleteInput_form {
      background-color: var(--inputControlDisabledBgColor) !important;
      color: var(--inputControlDisabledTextColor) !important;
      -webkit-text-fill-color: var(--inputControlDisabledTextColor) !important;
      border-color: var(--dark-panel-border) !important;
      opacity: 1 !important;
    }

    body[data-theme='dark'] .wlb-sliding-pane,
    body[data-theme='dark'] .slide-pane,
    body[data-theme='dark'] .slide-pane__content,
    body[data-theme='dark'] .slide-pane__header,
    body[data-theme='dark'] .slide-pane__footer {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
      opacity: 1 !important;
    }

    body[data-theme='dark'] .slide-pane__title,
    body[data-theme='dark'] .slide-pane__close,
    body[data-theme='dark'] .slide-pane__close svg,
    body[data-theme='dark'] .sliderMainPage .textField-Label,
    body[data-theme='dark'] .sliderMainPage .switch,
    body[data-theme='dark'] .sliderMainPage .subReasonNameText,
    body[data-theme='dark'] .sliderMainPage .subReasonText {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .sliderMainPage .MuiOutlinedInput-root,
    body[data-theme='dark'] .sliderMainPage .MuiInputBase-root,
    body[data-theme='dark'] .sliderMainPage .MuiInputBase-input,
    body[data-theme='dark'] .sliderMainPage .MuiAutocomplete-inputRoot {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-search-text) !important;
      border-color: var(--dark-search-border) !important;
    }

    body[data-theme='dark'] .sliderMainPage .MuiInputBase-input::placeholder {
      color: var(--dark-search-placeholder) !important;
      -webkit-text-fill-color: var(--dark-search-placeholder) !important;
      opacity: 1 !important;
    }

    body[data-theme='dark'] .sliderMainPage .sliderFooter {
      border-top: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .autocompleteMultiselectLazyLoadctrl,
    body[data-theme='dark'] .multidateandtime-container,
    body[data-theme='dark'] .dateTimePickerInputDiv,
    body[data-theme='dark'] .time-container {
      border: 1px solid var(--dark-search-border) !important;
      border-radius: 4px;
      // overflow: hidden !important;
      background-color: var(--dark-search-bg) !important;
    }

    body[data-theme='dark'] .multidateandtimeinput {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .rmdp-wrapper,
    body[data-theme='dark'] .rmdp-calendar,
    body[data-theme='dark'] .rmdp-top-class,
    body[data-theme='dark'] .rmdp-time-picker,
    body[data-theme='dark'] .rmdp-time-picker div,
    body[data-theme='dark'] .rmdp-time-picker div input,
    body[data-theme='dark'] .rmdp-month-picker,
    body[data-theme='dark'] .rmdp-year-picker,
    body[data-theme='dark'] .rmdp-panel,
    body[data-theme='dark'] .rmdp-ym {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .rmdp-header-values,
    body[data-theme='dark'] .rmdp-week-day,
    body[data-theme='dark'] .rmdp-day,
    body[data-theme='dark'] .rmdp-day span,
    body[data-theme='dark'] .rmdp-arrow,
    body[data-theme='dark'] .rmdp-arrow-container {
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .rmdp-day:not(.rmdp-deactive):not(.rmdp-disabled) span:hover,
    body[data-theme='dark'] .rmdp-day.rmdp-selected span,
    body[data-theme='dark'] .rmdp-ym .rmdp-day span:hover,
    body[data-theme='dark'] .rmdp-ym .rmdp-day.rmdp-selected span {
      background-color: var(--dark-search-action-bg) !important;
      color: #ffffff !important;
    }

    body[data-theme='dark'] .sendBriefingPage .inputControls .inputControlsRow .multidateandtime-container,
    body[data-theme='dark'] .sendBriefingPage .multidateandtime-container {
      background-color: var(--dark-search-bg) !important;
    }

    body[data-theme='dark'] .sendBriefingPage .inputControls .inputControlsRow .multidateandtime-container .multidateandtimeinput {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .time-range-container .time-container {
      border: none !important;
      border-radius: 0 !important;
    }

    body[data-theme='dark'] .autocompleteMultiselectLazyLoadinputDiv,
    body[data-theme='dark'] .multiselectChipsInputDiv,
    body[data-theme='dark'] .autocompleteMultiselectInput,
    body[data-theme='dark'] .timeinput {
      border: none !important;
    }

    /* .base variant: label is inside the outer container, so border must be on the inner inputDiv only */
    body[data-theme='dark'] .autocompleteMultiselectLazyLoadctrl.base {
      border: none !important;
      overflow: visible !important;
    }

    body[data-theme='dark'] .autocompleteMultiselectLazyLoadctrl.base .autocompleteMultiselectLazyLoadinputDiv {
      border: 1px solid var(--dark-search-border) !important;
      border-radius: 4px !important;
      background-color: var(--dark-search-bg) !important;
      overflow: hidden !important;
    }

    body[data-theme='dark'] .org_parent .textbox .MuiInputBase-root,
    body[data-theme='dark'] .org_field .dropzonearea div {
      background-color: var(--dark-search-bg) !important;
    }

    body[data-theme='dark'] .signature-container.dropZoneArea div {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .signature-container .uploaded-file-container {
      border: 1px solid var(--dark-search-border) !important;
    }

    body[data-theme='dark'] .signature-container.dropZoneArea .fileuploadparas {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .actionSwitch {
      border: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .priorityGroupDisplayMainSection {
      border: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .serviceActionScreen .horizontalLine {
      background-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .monitoringConfigContainer {
      background-color: var(--dark-panel-surface) !important;
      box-shadow: none !important;
      border: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .monitoringConfigContainer .monitoringTitle,
    body[data-theme='dark'] .monitoringConfigContainer .dashboardTitle,
    body[data-theme='dark'] .monitoringConfigContainer .fieldLabel,
    body[data-theme='dark'] .monitoringConfigContainer .categoryDescription {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark']
      .monitoringConfigContainer
      .filterField
      .autoCompleteLazyLoadctrl
      .autoCompleteInput,
    body[data-theme='dark']
      .monitoringConfigContainer
      .filterField
      .autoCompleteLazyLoadctrl
      .autoCompleteLazyLoadinputDiv,
    body[data-theme='dark']
      .monitoringConfigContainer
      .categoryFieldItem
      .MuiOutlinedInput-root {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-search-text) !important;
      border-color: var(--dark-search-border) !important;
    }

    body[data-theme='dark']
      .monitoringConfigContainer
      .categoryFieldItem
      .MuiOutlinedInput-notchedOutline {
      border-color: var(--dark-search-border) !important;
    }

    body[data-theme='dark'] .monitoringConfigContainer .categoryAccordion {
      background-color: var(--dark-panel-bg) !important;
      border: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .monitoringConfigContainer .actionButtons {
      background-color: var(--dark-panel-surface) !important;
      border-top: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .titleBar-root .title,
    body[data-theme='dark'] .titleBar-root .titleBarUser-avatar,
    body[data-theme='dark'] .titleBar-root .titleBarUserDropDown,
    // body[data-theme='dark'] .titleBar-root .userHeader,
    body[data-theme='dark'] .titleBar-root .MuiInputBase-root,
    body[data-theme='dark'] .titleBar-root .MuiInputBase-input {
      background-color: var(--dark-topbar-bg) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .ManageUserPage #Export,
    body[data-theme='dark'] .ManageUserPage .exportUsersButton,
    body[data-theme='dark'] .ManageUserPage .MuiButton-root.exportUsersButton {
      background-color: var(--dark-search-action-bg) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-search-action-bg) !important;
    }

    body[data-theme='dark'] .modalmainpage {
      background-color: var(--dark-panel-bg) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .modalmainpage .closeicon svg,
    body[data-theme='dark'] .modalmainpage .closeicon .MuiSvgIcon-root {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .attachPopup,
    body[data-theme='dark'] .dialogContainer {
      background-color: var(--dark-panel-bg) !important;
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .attachPopup .dropzonearea {
      background-color: var(--dark-panel-surface) !important;
      border-color: var(--dark-search-border) !important;
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .attachText {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .fileuploadparas,
    body[data-theme='dark'] .fileTypeText,
    body[data-theme='dark'] .noteText,
    body[data-theme='dark'] .addedFileName {
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .infoText {
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .attachPopup .MuiCheckbox-root,
    body[data-theme='dark'] .attachPopup .MuiCheckbox-root svg {
      color: var(--dark-text-secondary) !important;
      fill: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .attachPopup .MuiFormControlLabel-root .MuiTypography-root,
    body[data-theme='dark'] .attachPopup .MuiFormControlLabel-root.Mui-disabled .MuiTypography-root {
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .MuiCheckbox-root.Mui-disabled,
    body[data-theme='dark'] .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root,
    body[data-theme='dark'] .MuiFormControlLabel-root.Mui-disabled .MuiTypography-root,
    body[data-theme='dark'] .MuiFormControlLabel-root .MuiCheckbox-root.Mui-disabled + .MuiTypography-root {
      color: var(--inputControlDisabledTextColor) !important;
      fill: var(--inputControlDisabledTextColor) !important;
      -webkit-text-fill-color: var(--inputControlDisabledTextColor) !important;
      opacity: 1 !important;
    }

    body[data-theme='dark'] .attachPopup .deleteIcon svg,
    body[data-theme='dark'] .attachPopup .deleteIcon .MuiSvgIcon-root {
      color: var(--dark-text-secondary) !important;
      fill: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .attachPopup .closeIcon svg,
    body[data-theme='dark'] .attachPopup .closeIcon .MuiSvgIcon-root {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .MuiAccordionSummary-content {
      background-color: transparent !important;
    }

    body[data-theme='dark'] [data-runtime-dark='1'],
    body[data-theme='dark'] [data-runtime-dark='surface'] {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] [data-runtime-dark='input'] {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-search-text) !important;
      border-color: var(--dark-search-border) !important;
    }

    body[data-theme='dark'] [data-runtime-dark='input']::placeholder {
      color: var(--dark-search-placeholder) !important;
      -webkit-text-fill-color: var(--dark-search-placeholder) !important;
    }

    body[data-theme='dark'] [data-runtime-dark-text='1']:not(.mandatorySymbol,.mandatory-symbol,.required-asterisk,
    .Required-color,.required,.caseOverviewPage .tabHeader .Mui-selected .tabTitleButton.contained,.requiredClass,.messageListPagePopup .requiredClass,.required,.error,.Mui-error,.MuiFormHelperText-root,.MuiFormHelperText-root.Mui-error,.validation-icon-error,.toast-root.error,.MuiAlert-root.MuiAlert-outlinedError,.MuiAlert-root.MuiAlert-filledError):not(.toast-root.error *):not(.MuiAlert-root.MuiAlert-outlinedError *):not(.MuiAlert-root.MuiAlert-filledError *)
    {
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .caseOverviewPage .tabHeader .Mui-selected .tabTitleButton.contained{    
      color: #ffffff !important;
    }

    body[data-theme='dark'] .foreignDebtPopupCloseIcon svg path {
      fill: #ffffff !important;
    }
    body[data-theme='dark'] .caseOverviewTab .MuiButton-root.exportCaseOverviewButton,
    body[data-theme='dark'] .BellNotification svg,
    body[data-theme='dark'] .BellNotification svg path {
      color: #3276CF !important;
      fill: #3276CF !important;
    }

    body[data-theme='dark'] .caseOverviewTab .MuiButton-root.exportCaseOverviewButton,
    body[data-theme='dark'] .caseOverviewTab .MuiButton-root.exportCaseOverviewButton * {
      color: #3276CF !important;
      -webkit-text-fill-color: #3276CF !important;
    }

    body[data-theme='dark'] .BellNotification .MuiSvgIcon-colorAction,
    body[data-theme='dark'] .BellNotification .MuiSvgIcon-colorDisabled,
    body[data-theme='dark'] .BellNotification .MuiSvgIcon-root,
    body[data-theme='dark'] .BellNotification .MuiSvgIcon-root path {
      color: #3276CF !important;
      fill: #3276CF !important;
      stroke: #3276CF !important;
    }

    body[data-theme='dark'] .titleBar-root .notificationDiv .BellNotification .MuiSvgIcon-root,
    body[data-theme='dark'] .titleBar-root .notificationDiv .BellNotification .MuiSvgIcon-root path {
      color: #3276CF !important;
      fill: #3276CF !important;
      stroke: #3276CF !important;
    }

    body[data-theme='dark'] .titleBar-root .titleBarUserDropDown,
    body[data-theme='dark'] .caseOverviewPage .refreshSection .caseOverviewRefresh,
    body[data-theme='dark'] .gridPageRefresh
    {
      filter: brightness(0) saturate(100%) invert(45%) sepia(72%) saturate(918%) hue-rotate(187deg) brightness(88%) contrast(90%) !important;
    }

    body[data-theme='dark'] .pcnPage .next_previous_btn .next_img,
    body[data-theme='dark'] .pcnPage .next_previous_btn .previous_img {
      filter: brightness(0) saturate(100%) invert(45%) sepia(72%) saturate(918%) hue-rotate(187deg) brightness(88%) contrast(90%) !important;
    }

    body[data-theme='dark'] .titleBar-root .avataricon .MuiAvatar-root.Main-Avatar,
    body[data-theme='dark'] .titleBar-root .avataricon .MuiAvatar-root {
      background-color: #3276CF !important;
      color: #ffffff !important;
      border: 1px solid #3276CF !important;
      filter: none !important;
    }

    body[data-theme='dark'] .contraventionsection .gpsLocation .MuiSvgIcon-root,
    body[data-theme='dark'] .contraventionsection .gpsLocation .MuiSvgIcon-root path,
    body[data-theme='dark'] .contraventionsection .gpsLocation .MuiInputAdornment-root svg,
    body[data-theme='dark'] .contraventionsection .gpsLocation .MuiInputAdornment-root svg path {
      color: #3276CF !important;
      fill: #3276CF !important;
      stroke: #3276CF !important;
      opacity: 1 !important;
    }

    body[data-theme='dark'] .pcnsection .button.MuiButton-secondary,
    body[data-theme='dark'] .pcnsection .button.MuiButton-secondary .buttonName,
    body[data-theme='dark'] .pcnsection .button.MuiButton-secondary .MuiSvgIcon-root {
      color: #3276CF !important;
      border-color: #3276CF !important;
      fill: #3276CF !important;
    }

    body[data-theme='dark'] .radioGroupControl .MuiRadio-root,
    body[data-theme='dark'] .radioGroupControl .MuiRadio-root .MuiSvgIcon-root,
    body[data-theme='dark'] .radioGroupControl .MuiRadio-root .MuiSvgIcon-root path,
    body[data-theme='dark'] .RadioGroupControl .MuiRadio-root,
    body[data-theme='dark'] .RadioGroupControl .MuiRadio-root .MuiSvgIcon-root,
    body[data-theme='dark'] .RadioGroupControl .MuiRadio-root .MuiSvgIcon-root path {
      color: #3276CF !important;
      fill: #3276CF !important;
      stroke: #3276CF !important;
    }

    body[data-theme='dark'] .rangeSelectDate button#customDatesCalenderOpen:hover,
    body[data-theme='dark'] .rangeSelectDate button#customDatesRangeSelectionOpen:hover {
    background-color: var(--dark-panel-surface) !important;
    }

    body[data-theme='dark'] img[alt="Preview"], body[data-theme='dark'] .contraventionsection img {
    filter: brightness(0) invert(1);
    }

    body[data-theme='dark'] .reportFaultAttachmentContainer
    .reportFaultWithoutAttachment .dropzonearea div{
    background-color: var(--dark-panel-surface) !important;
    color: var(--dark-text-primary) !important;
    }
     body[data-theme='dark'] #addAttachmentPopup .chipDiv {

     }

    body[data-theme='dark'] .fileFormatImg {
    filter: brightness(0) invert(1);
    }

    body[data-theme='dark'] .manualCaseCreationPage .stepperImage {
      background: white !important;
    }

    body[data-theme='dark'] .caseOverviewTab .caseOverviewHeader .MuiButtonBase-root .MuiSvgIcon-root
    {
      fill: #3276CF !important;
    }

    body[data-theme='dark'] img,
    body[data-theme='dark'] svg {
      background-color: transparent !important;
    }

    body[data-theme='dark'] .deleteIconAira,
    body[data-theme='dark'] .MuiInputAdornment-root img {
      filter: brightness(0) invert(1) !important;
    }

    body[data-theme='dark'] .jodit-toolbar-button__button svg,
    body[data-theme='dark'] .jodit-toolbar-button__button svg path,
    body[data-theme='dark'] .jodit-toolbar-button__trigger svg,
    body[data-theme='dark'] .jodit-toolbar-button__trigger svg path {
      fill: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .pcnNumberStatusCompleted {
    background: var(--statusCompleted-background) !important;;
    color: var(--statusCompleted-fontcolor) !important;;
    }
    body[data-theme='dark'] .pcnNumberStatusInitiated {  
      background: var(--statusInitiated-background) !important;
      color: var(--button-color) !important;
    }

    body[data-theme='dark'] ::highlight(jodit-search-result) {
      background-color: #2563a8;
      color: #ffffff;
    }

    body[data-theme='dark'] .currentDayHighlight span.sd,
    body[data-theme='dark'] .rmdp-day.currentDayHighlight span {
      background-color: var(--dark-search-action-bg) !important;
      color: #ffffff !important;
    }

    body[data-theme='dark'] .officerTracking .taskTitleDiv .active {
      background-color: var(--dark-search-action-bg) !important;
      color: #ffffff !important;
    }

    body[data-theme='dark'] .stepper-progress img {
      filter: brightness(0.75) contrast(1.2) !important;
    }

    body[data-theme='dark'] .airaLetterContentSettingsContainer .nextButtonContainer, body[data-theme='dark'] .templateContent.abandonedVehicles .abandonedVehicleList .abvStatusChip.error,
    body[data-theme='dark'] .LocationListContent .MuiButton-root.exportLocationButton,
    body[data-theme='dark'] .zonePage .MuiButton-root.exportZoneButton,
    body[data-theme='dark'] .cancellationReasonPage .MuiButton-root.exportCancellationReasonButton,
    body[data-theme='dark'] .rejectReasonPage .MuiButton-root.exportRejectReasonButton,
    body[data-theme='dark'] .holdreason-content .MuiButton-root.exportholdReasonButton,
    body[data-theme='dark'] .CRcancellationReasonPage .MuiButton-root.exportCancellationReasonButton,
    body[data-theme='dark'] .releaseReasonListContainer .MuiButton-root.exportCancellationReasonButton
    {
      background-color: var(--dark-panel-surface) !important;
    }

    body[data-theme='dark'] .offence-details-section .contravention-edit-container .autoCompleteLazyLoadctrl .autoCompleteInput, body[data-theme='dark'] .textbox .vrm-input .MuiInputBase-root.MuiInput-root.Mui-disabled, 
    body[data-theme='dark'] .fault-status-update-dialog .fault-status-update-dialog__field .autoCompleteLazyLoadinputDiv input, body[data-theme='dark'] .fault-status-update-dialog .fault-status-update-dialog__field .multilinetextbox textarea,
    body[data-theme='dark'] .fault-status-update-dialog .fault-status-update-dialog__field .autoCompleteLazyLoadinputDiv
    {
      background-color: var(--dark-search-bg) !important;
    }

    body[data-theme='dark'] .nextButtonContainer .nextButton svg.arrowIcon,
    body[data-theme='dark'] .nextButtonContainer .nextButton svg.arrowIcon path {
      fill: var(--dark-text-primary) !important;
      color: var(--dark-text-primary) !important;
    }

    
body[data-theme='dark'] .offence-details-section .contravention-edit-icon,
body[data-theme='dark'] .offence-details-section .contravention-edit-icon svg,
body[data-theme='dark'] .offence-details-section .contravention-edit-icon svg path {
  fill: #3276cf !important;
  stroke: #3276cf !important;
  color: #3276cf !important;
}


body[data-theme='dark'] .vrm-details-container .vrm-icon,
body[data-theme='dark'] .vrm-details-container .vrm-icon svg,
body[data-theme='dark'] .vrm-details-container .vrm-icon svg path,
body[data-theme='dark'] .vrm-details-container .vrm-edit-icon,
body[data-theme='dark'] .vrm-details-container .vrm-edit-icon svg,
body[data-theme='dark'] .vrm-details-container .vrm-edit-icon svg path {
  fill: #3276cf !important;
  stroke: #3276cf !important;
  color: #3276cf !important;
}

body[data-theme='dark'] .vrm-details-container .edit-title,
body[data-theme='dark'] .vrm-details-container .edit-title-text {
  color: #3276cf !important;
}

body[data-theme='dark']
  .manual-review-detail-page
  .manual-review-page__back-icon {
  /* Keep back arrow visible on dark backgrounds. */
  filter: brightness(0) invert(1);
}

    
    /* Keep browser autofilled fields on dark surfaces (Chrome/WebKit). */
    body[data-theme='dark'] input:-webkit-autofill,
    body[data-theme='dark'] input:-webkit-autofill:hover,
    body[data-theme='dark'] input:-webkit-autofill:focus,
    body[data-theme='dark'] input:-webkit-autofill:active,
    body[data-theme='dark'] textarea:-webkit-autofill,
    body[data-theme='dark'] textarea:-webkit-autofill:hover,
    body[data-theme='dark'] textarea:-webkit-autofill:focus,
    body[data-theme='dark'] select:-webkit-autofill,
    body[data-theme='dark'] .MuiInputBase-input:-webkit-autofill,
    body[data-theme='dark'] .MuiInputBase-input:-webkit-autofill:hover,
    body[data-theme='dark'] .MuiInputBase-input:-webkit-autofill:focus,
    body[data-theme='dark'] .MuiOutlinedInput-input:-webkit-autofill,
    body[data-theme='dark'] .MuiOutlinedInput-input:-webkit-autofill:hover,
    body[data-theme='dark'] .MuiOutlinedInput-input:-webkit-autofill:focus,
    body[data-theme='dark'] .MuiFilledInput-input:-webkit-autofill,
    body[data-theme='dark'] .MuiFilledInput-input:-webkit-autofill:hover,
    body[data-theme='dark'] .MuiFilledInput-input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px var(--dark-search-bg) inset !important;
      box-shadow: 0 0 0 1000px var(--dark-search-bg) inset !important;
      -webkit-text-fill-color: var(--dark-search-text) !important;
      color: var(--dark-search-text) !important;
      background-color: var(--dark-search-bg) !important;
      border-color: var(--dark-search-border) !important;
      caret-color: var(--dark-search-text) !important;
      transition: background-color 9999s ease-in-out 0s;
    }

    /* Challenge Overview page — containers & cards */
    body[data-theme='dark'] .challenge-overview-container {
      background: var(--dark-panel-bg) !important;
    }
    body[data-theme='dark'] .challenge-validation-card,
    body[data-theme='dark'] .challenge-ocr-card,
    body[data-theme='dark'] .challenge-summary-card,
    body[data-theme='dark'] .challenge-agents-card {
      background: var(--dark-panel-surface) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* ApexCharts SVG axis text (VRM / Make & Color labels, numbers, week labels) */
    body[data-theme='dark'] .challenge-overview-container .apexcharts-text,
    body[data-theme='dark'] .challenge-overview-container .apexcharts-yaxis-label,
    body[data-theme='dark'] .challenge-overview-container .apexcharts-xaxis-label {
      fill: var(--dark-text-primary) !important;
    }

    /* ApexCharts grid lines */
    body[data-theme='dark'] .challenge-overview-container .apexcharts-gridline,
    body[data-theme='dark'] .challenge-overview-container .apexcharts-xaxis-tick {
      stroke: var(--dark-panel-border) !important;
    }

    /* Bar legend "Valid / Not valid / Can't valid" text */
    body[data-theme='dark'] .challenge-bar-legend span {
      color: var(--dark-text-primary) !important;
    }

    /* OCR section */
    body[data-theme='dark'] .challenge-ocr-percent {
      color: var(--dark-text-primary) !important;
    }
    body[data-theme='dark'] .challenge-ocr-bar-label,
    body[data-theme='dark'] .challenge-ocr-bar-value {
      color: var(--dark-text-primary) !important;
    }
    body[data-theme='dark'] .challenge-ocr-divider {
      background: var(--dark-panel-border) !important;
    }
    body[data-theme='dark'] .challenge-ocr-bar-track {
      background: var(--dark-search-border) !important;
    }

    /* Agents Interventions — inactive tab buttons */
    body[data-theme='dark'] .challenge-agents-tabs button:not(.active) {
      background: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* Agents legend text */
    body[data-theme='dark'] .challenge-agents-legend-label,
    body[data-theme='dark'] .challenge-agents-legend-count {
      color: var(--dark-text-primary) !important;
    }

    /* PCN header — Stage & Status: label=blue, value=white in dark mode */
    body[data-theme='dark'] .div_Headersection .divStatus .divsubTitle,
    body[data-theme='dark'] .debtorReviewId .divsubTitle {
      color: var(--dark-search-action-bg) !important;
    }
    body[data-theme='dark'] .div_Headersection .divStatus .divsubValue,
    body[data-theme='dark'] .debtorReviewId .divsubValue {
      color: var(--dark-text-primary) !important;
    }

    /* Action button SVG icons (TEC / Administration / Challenge) — blue in dark mode (active + disabled) */
    body[data-theme='dark'] .actionButton .actionHeader .actionIcon svg path {
      fill: var(--dark-search-action-bg) !important;
    }

    /* Action buttons — border + distinct enabled/disabled backgrounds */
    body[data-theme='dark'] .actionButton {
      border: 1px solid var(--dark-panel-border) !important;
    }
    body[data-theme='dark'] .actionButton:not(.disabled) {
      background-color: #1c2329 !important;
    }
    body[data-theme='dark'] .actionButton.disabled {
      background-color: #2d3d4f !important;
    }
    body[data-theme='dark'] .actionButton.disabled .actionHeader .actionTitleDiv .actionTitle,
    body[data-theme='dark'] .actionButton.disabled .actionContent .actionDescription {
      background-color: #2d3d4f !important;
    }
    body[data-theme='dark'] .actionButton.disabled .actionHeader .actionIcon svg path {
      fill: var(--dark-search-border) !important;
    }

    /* Accordion expand/collapse chevron arrows — white in dark mode */
    body[data-theme='dark'] .expandCollapseIcon svg path,
    body[data-theme='dark'] .expandCollapseIcon svg {
      fill: var(--dark-text-primary) !important;
    }

    /* Sidebar active/opened menu item — preserve border radius, use dark bg */
    body[data-theme='dark'] .outeranchorgroup.opened,
    body[data-theme='dark'] .outeranchor.active {
      border-top-right-radius: 30px !important;
      border-bottom-right-radius: 30px !important;
      background-color: var(--dark-panel-surface) !important;
    }

    body[data-theme='dark'] .drawer__submenu_item.active, 
    body[data-theme='dark'] .drawer__mainmenu_item.active {
    border-top-right-radius: 30px !important;
    border-bottom-right-radius: 30px !important;
    }

   body[data-theme='dark'] .homepagemainsection
  .homepagesubsection
  .homepagetoolsection
  .switch
  .switch_unStyle
  .MuiSwitch-thumb,body[data-theme='dark'] .switch .switch_unStyle .MuiSwitch-thumb, body[data-theme='dark'] .reportConfigurationContainer .switch .switch_unStyle .Mui-checked .MuiSwitch-track, body[data-theme='dark'] .switch .Mui-checked .MuiSwitch-track {
   background-color: #3276CF !important;
   }
   body[data-theme='dark'] .homepagemainsection
  .homepagesubsection
  .homepagetoolsection
  .switch
  .Mui-checked
  .MuiSwitch-thumb,body[data-theme='dark'] .reportConfigurationContainer .switch
  .Mui-checked
  .MuiSwitch-thumb,body[data-theme='dark'] .switch .switch_unStyle .Mui-checked .MuiSwitch-thumb{
   background-color: white !important;
   }
   
   body[data-theme='dark'] .switch .switch_unStyle .MuiSwitch-track,
   body[data-theme='dark'] .reportConfigurationContainer .switch .switch_unStyle .MuiSwitch-track {
   border: 1px solid #3276CF !important;
   }

   body[data-theme='dark']  .homepagemainsection .homepagesubsection .homepagetoolsection .switch .Mui-checked .MuiSwitch-thumb,
   body[data-theme='dark'] .switch .Mui-checked .MuiSwitch-thumb,  
   {
    background-color: white !important;;
  }

   body[data-theme="dark"] .btn--primary.button.MuiButton-root:disabled {
  background-color: #3A4652 !important;
  color: #9FAAB5 !important;
  border: 1px solid #4A5662;
  cursor: not-allowed;
}

   body[data-theme="dark"] .columnFilterImage {
  filter: brightness(0) invert(1);
}

   body[data-theme="dark"] .taskTitleDiv, body[data-theme="dark"] .taskTitleName  {
  border: 1px solid var(--dark-panel-border);
}
  body[data-theme="dark"] .taskTitleName.active {
  background-color: #3276CF;
  color: #fff;
  border: 1px solid #3276CF;
  border-radius: 6px;
}
  body[data-theme="dark"] .MuiButton-root.btn--secondary:hover {
  color: inherit;
  background-color: inherit;
  }

   /* Branding (Basic/Advanced) Add URL button: prevent white flash and fix disabled colors */
   body[data-theme='dark'] .divURLAdd #AddURL,
   body[data-theme='dark'] .divAdvancedURLAdd #AddURL,
   body[data-theme='dark'] .divURLAdd .btn_Add_URL .MuiButton-root,
   body[data-theme='dark'] .divAdvancedURLAdd .btn_Add_URL .MuiButton-root {
    background-color: var(--dark-panel-surface) !important;
    border: 1px solid var(--dark-shell-border) !important;
   }

   body[data-theme='dark'] .divURLAdd #disabled_addURL,
   body[data-theme='dark'] .divAdvancedURLAdd #disabled_addURL,
   body[data-theme='dark'] .divURLAdd .MuiButton-root.Mui-disabled,
   body[data-theme='dark'] .divAdvancedURLAdd .MuiButton-root.Mui-disabled {
    background-color: #3A4652 !important;
    color: #9FAAB5 !important;
    border: 1px solid #4A5662 !important;
    opacity: 1 !important;
    cursor: not-allowed !important;
   }

    body[data-theme='dark'] .autocompleteMultiselectLazyLoadctrl .autocompleteMultiselectLazyLoadinputDiv .multiselectChipsInputDiv .MuiChip-root{
   background-color: #3A4652;
    }

    /* CR Charge modal: Add new charge type button - keep disabled style stable in dark mode */
    body[data-theme='dark'] .chargeCrudContainer .chargeCrudFooter .addChargeTypeSection .button.MuiButton-root {
      transition: background-color 0s, color 0s, border-color 0s !important;
    }

    body[data-theme='dark'] .chargeCrudContainer .chargeCrudFooter .addChargeTypeSection .button.MuiButton-root:disabled,
    body[data-theme='dark'] .chargeCrudContainer .chargeCrudFooter .addChargeTypeSection .button.MuiButton-root.Mui-disabled {
      background-color: #3A4652 !important;
      color: #9FAAB5 !important;
      border: 1px solid #4A5662 !important;
      opacity: 1 !important;
      cursor: not-allowed !important;
      padding: 8px !important;
    }

    body[data-theme='dark'] .chargeCrudContainer .chargeCrudFooter .addChargeTypeSection .button.MuiButton-root.Mui-disabled .MuiSvgIcon-root,
    body[data-theme='dark'] .chargeCrudContainer .chargeCrudFooter .addChargeTypeSection .button.MuiButton-root.Mui-disabled .MuiSvgIcon-root path,
    body[data-theme='dark'] .chargeCrudContainer .chargeCrudFooter .addChargeTypeSection .button.MuiButton-root:disabled .MuiSvgIcon-root,
    body[data-theme='dark'] .chargeCrudContainer .chargeCrudFooter .addChargeTypeSection .button.MuiButton-root:disabled .MuiSvgIcon-root path {
      color: #9FAAB5 !important;
      fill: #9FAAB5 !important;
      stroke: #9FAAB5 !important;
    }

  body[data-theme='dark'] .divURLAdd #disabled_addURL .MuiSvgIcon-root,
  body[data-theme='dark'] .divAdvancedURLAdd #disabled_addURL .MuiSvgIcon-root,
  body[data-theme='dark'] .divURLAdd .MuiButton-root.Mui-disabled .MuiSvgIcon-root,
  body[data-theme='dark'] .divAdvancedURLAdd .MuiButton-root.Mui-disabled .MuiSvgIcon-root,
  body[data-theme='dark'] .divURLAdd #disabled_addURL .MuiSvgIcon-root path,
  body[data-theme='dark'] .divAdvancedURLAdd #disabled_addURL .MuiSvgIcon-root path,
  body[data-theme='dark'] .divURLAdd .MuiButton-root.Mui-disabled .MuiSvgIcon-root path,
  body[data-theme='dark'] .divAdvancedURLAdd .MuiButton-root.Mui-disabled .MuiSvgIcon-root path {
   color: #9FAAB5 !important;
   fill: #9FAAB5 !important;
   stroke: #9FAAB5 !important;
  }
  
    body[data-theme='dark'] img.searchiconimage,
    body[data-theme='dark'] .ui.search .prompt ~ .search.icon {
      background-color: #3276CF !important;
      border: 1px solid #3276CF;
    }

    body[data-theme='dark'] .button.MuiButton-secondary {
    background: var(--dark-panel-bg)
    }

    /* Activity Days: rule selector uses --userCountBand-bg-color in page CSS (light token). */
    body[data-theme='dark'] .activity_days_setup
      .activity_autocompletefiled_rule
      .autoCompleteLazyLoadctrl
      .autoCompleteLazyLoadinputDiv,
    body[data-theme='dark'] .activity_days_setup
      .activity_autocompletefiled_rule
      .autoCompleteLazyLoadctrl
      .autoCompleteInput,
    body[data-theme='dark'] .activity_days_setup
      .activity_autocompletefiled_rule
      .autoCompleteLazyLoadctrl
      .autoCompleteInput::placeholder {
      background-color: var(--dark-search-bg) !important;
      color: var(--dark-search-text) !important;
      -webkit-text-fill-color: var(--dark-search-text) !important;
      border-color: var(--dark-search-border) !important;
    }

    body[data-theme='dark'] .activity_days_setup
      .activity_autocompletefiled_rule
      .autoCompleteLazyLoadctrl
      .MuiSvgIcon-root,
    body[data-theme='dark'] .activity_days_setup
      .activity_autocompletefiled_rule
      .autoCompleteLazyLoadctrl
      .MuiSvgIcon-root path {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
    }

    body[data-theme='light'] .activity_days_setup .activitydaysection,
    body[data-theme='light'] .activity_days_setup .arrowBack .arrowbackbutton,
    body[data-theme='light'] .activity_days_setup .arrowBack .arrowbackbutton svg,
    body[data-theme='light'] .activity_days_setup .arrowBack .arrowbackbutton svg path {
      color: var(--default-FontColour) !important;
      fill: var(--default-FontColour) !important;
      -webkit-text-fill-color: var(--default-FontColour) !important;
    }

    body[data-theme='dark'] .activity_days_setup .activitydaysection,
    body[data-theme='dark'] .activity_days_setup .arrowBack .arrowbackbutton,
    body[data-theme='dark'] .activity_days_setup .arrowBack .arrowbackbutton svg,
    body[data-theme='dark'] .activity_days_setup .arrowBack .arrowbackbutton svg path {
      color: var(--dark-text-primary) !important;
      fill: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .boApplication,
    body[data-theme='dark'] .bodypage,
    body[data-theme='dark'] .body-data-page,
    body[data-theme='dark'] .drawer__mainmenu__navgroup,
    body[data-theme='dark'] .titlebar {
      color: var(--dark-text-primary) !important;
      background-color: var(--dark-panel-bg) !important;
      border-color: var(--dark-panel-border) !important;
    }
    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-row:hover,
     body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: var(--dark-panel-bg) !important;
    }

    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-row:nth-of-type(even) {
      background-color: #31393f !important;
    }

    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell {
      background-color: #31393f !important;
    }

    body[data-theme='dark'] .MuiDataGrid-root {
      border: none !important;
    }

    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-columnHeader,
    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-cell,
    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-columnHeaders,
    body[data-theme='dark'] .MuiDataGrid-root .MuiDataGrid-footerContainer,
    body[data-theme='dark'] .MuiDataGrid-root [class*="withBorderColor"] {
      border: none !important;
    }

    body[data-theme='dark'] .priorityField {
    color: #ffffff !important;
    }

    body[data-theme='dark'] .MuiIconButton-root.MuiAutocomplete-popupIndicator svg path,
    body[data-theme='dark'] .MuiButtonBase-root .MuiSvgIcon-root {
    fill: #FFFFFF !important;
    }
    body[data-theme='dark'] .columnsPanelTrigger-label {
      color: var(--dark-text-primary) !important;
    }
    body[data-theme='dark'] .homepagemainsection,
    body[data-theme='dark'] .workspace-page {
      border-radius: 40px !important;
      background: #31393f !important;
    }

    body[data-theme='dark'] .homepagecardsection .MuiPaper-root,
    body[data-theme='dark']
      .homepagemainsection
      .homepagecardsection
      .MuiPaper-root.MuiAccordion-root {
      background: #31393f !important;
    }

    body[data-theme='dark'] .homepagecard,
    body[data-theme='dark'] .homepagecardsection .MuiCard-root,
    body[data-theme='dark'] .homepagemainsection .cardcontentsection,
    body[data-theme='dark']
      .homepagemainsection
      .homepagesubsection
      .homepagecardsection
      .MuiCardContent-root,
    body[data-theme='dark']
      .homepagemainsection
      .homepagetoolsection
      .textbox
      .MuiInputBase-root {
      background-color: var(--dark-panel-bg) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .workspace-page .tabHeader,
    body[data-theme='dark'] .tabControl .tabHeader {
      background-color: #262d33 !important;
    }

    body[data-theme='dark'] .workspace-page .tabHeader .tabTitleButton.contained,
    body[data-theme='dark'] .tabControl .tabHeader .tabTitleButton.text,
    body[data-theme='dark'] .workspace-page .tabHeader .tabTitleButton.text,
    body[data-theme='dark']
      .homepagemainsection
      .homepagesubsection
      .homepagecardsection
      .accordionTitle {
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark']
      .workspace-page
      .tabHeader
      .Mui-selected
      .tabTitleButton.contained,
    body[data-theme='dark']
      .workspace-page
      .tabControl
      .tabHeader
      .MuiButtonBase-root.Mui-selected,
    body[data-theme='dark']
      .tabControl
      .tabHeader
      .MuiButtonBase-root.Mui-selected,
    body[data-theme='dark']
      .tabHeader
      .Mui-selected
      .tabTitleButton.contained {
      color: #ffffff !important;
      border-bottom-color: var(--dark-search-action-bg) !important;
    }

    body[data-theme='dark']
      .homepagemainsection
      .MuiCardHeader-root
      .MuiCardHeader-title,
    body[data-theme='dark'] .carddatasection .carddatasectiondata {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark']
      .homepagemainsection
      .MuiCardHeader-root
      .MuiCardHeader-subheader,
    body[data-theme='dark'] .carddatasection .carddatasectiontitle,
    body[data-theme='dark'] .homepagemainsection .homepagetoolsection,
    body[data-theme='dark'] .homepagemainsection .homepagetoolsection .switch,
    body[data-theme='dark']
      .homepagemainsection
      .homepagetoolsection
      .switch
      .text {
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .drawer__submenu_item.active,
    body[data-theme='dark'] .drawer__mainmenu_item.active,
    body[data-theme='dark'] .drawer .menu li a:hover {
      background-color: var(--dark-active-menu-bg) !important;
    }

    body[data-theme='dark'] .titleBar-root,
    body[data-theme='dark'] .titleBar-root .usernamesection,
    body[data-theme='dark'] .titleBar-root .titleBar-icon,
    body[data-theme='dark'] .titleBar-root .titleBar-iconList {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .drawer .menu li a,
    body[data-theme='dark'] .drawer .menu :where(.levelOne, .levelTwo) {
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .homepagemainsection .homepagecardsection .MuiCard-root,
    body[data-theme='dark'] .homepagemainsection .homepagecard {
      background-color: #2a323a !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] input,
    body[data-theme='dark'] textarea {
      color: var(--dark-search-text) !important;
      background-color: var(--dark-search-bg) !important;
    }

    body[data-theme='dark'] input::placeholder,
    body[data-theme='dark'] textarea::placeholder {
      color: var(--dark-search-placeholder) !important;
      -webkit-text-fill-color: var(--dark-search-placeholder) !important;
    }

    body[data-theme='dark'] .ag-body-vertical-scroll,
    body[data-theme='dark'] .ag-body-horizontal-scroll,
    body[data-theme='dark'] .ag-scrollbar {
      background-color: var(--dark-panel-bg) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .ag-scrollbar-thumb,
    body[data-theme='dark']
      .ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb,
    body[data-theme='dark']
      .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb {
      background-color: var(--scrollBarBgColor) !important;
    }

    body[data-theme='dark'] *:not(.error, .Mui-error, .MuiFormHelperText-root, .MuiFormHelperText-root.Mui-error, .validation-icon-error, .toast-root.error, .MuiAlert-root.MuiAlert-outlinedError, .MuiAlert-root.MuiAlert-filledError):not(.toast-root.error *):not(.MuiAlert-root.MuiAlert-outlinedError *):not(.MuiAlert-root.MuiAlert-filledError *):not(.actionButton .actionHeader .actionTitleDiv .actionTitle):not(.actionButton .actionContent .actionDescription):not(.div_Headersection .divStatus .divsubTitle):not(.div_Headersection .divStatus .divsubValue):not(.debtorReviewId .divsubTitle):not(.debtorReviewId .divsubValue) :not(:disabled) {
      scrollbar-color: var(--scrollBarBgColor)  var(--dark-panel-bg);     
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .div_Headersection .divStatus .divsubTitle,
    body[data-theme='dark'] .debtorReviewId .divsubTitle {
      color: var(--dark-search-action-bg) !important;
      -webkit-text-fill-color: var(--dark-search-action-bg) !important;
    }

    body[data-theme='dark'] .div_Headersection .divStatus .divsubValue,
    body[data-theme='dark'] .debtorReviewId .divsubValue {
      color: var(--dark-text-primary) !important;
      -webkit-text-fill-color: var(--dark-text-primary) !important;
    }

    // body[data-theme='dark'] .actionButton .actionHeader .actionTitleDiv .actionTitle,
    body[data-theme='dark'] .actionButton .actionContent .actionDescription {
      color: var(--default-fontColor) !important;
      -webkit-text-fill-color: var(--default-fontColor) !important;
    }

    body[data-theme='dark'] .gridLinkclass , body[data-theme='dark'] .downloadComponent .fileName,
    body[data-theme='dark'] span.gridCellDisplayData.hyperlink.primaryColor,
    body[data-theme='dark'] .sendCorrespondence_new .linkButtonText,body[data-theme='dark'] .sendCorrespondence_new .linkButtonText path,
    body[data-theme='dark'] .case-validation-container .buttonContainer .MuiButton-text.MuiButton-textPrimary ,
    body[data-theme='dark'] .challenge-sub-section .StageTwoButtonComponentMainSection .MuiButtonBase-root .MuiSvgIcon-root{
    color: #3276CF !important;
    }

    body[data-theme='dark'] .challenge-sub-section .StageTwoButtonComponentMainSection .MuiButtonBase-root .MuiSvgIcon-root {
    fill: #3276CF !important;
}

    body[data-theme='dark'] .userDataSection .roleSetup {
    border: 1px solid;
    }

    body[data-theme='dark'] *::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }

    body[data-theme='dark'] *::-webkit-scrollbar-track {
      background: var(--dark-panel-bg);
    }

    body[data-theme='dark'] *::-webkit-scrollbar-thumb {
      background: var(--scrollBarBgColor);
      border-radius: 10px;
      border: 2px solid var(--dark-panel-bg);
    }

    body[data-theme='dark'] #ddlPerPageValue,
    body[data-theme='dark'] .gridPageSize #ddlPerPageValue,
    body[data-theme='dark']
      .gridPageSize
      .MuiFormControl-root.dropdown
      .MuiFilledInput-input {
      color: var(--dark-search-text) !important;
      background-color: var(--dark-search-bg) !important;
      border-color: var(--dark-search-border) !important;
    }

    body[data-theme='dark'] .gridPageSize .MuiSelect-icon,
    body[data-theme='dark'] .gridPageSize .MuiNativeSelect-icon {
      color: var(--dark-text-secondary) !important;
      fill: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .task-status-card {
      border: 1px solid var(--dark-panel-border) !important;
      background-color: var(--dark-panel-surface) !important;
    }

    body[data-theme='dark'] .task-status-label {
      color: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .task-status-value {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .recharts-cartesian-axis-tick-value,
    body[data-theme='dark'] .recharts-text {
      fill: var(--dark-text-secondary) !important;
    }

    body[data-theme='dark'] .recharts-cartesian-grid-horizontal line,
    body[data-theme='dark'] .recharts-cartesian-grid-vertical line {
      stroke: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .recharts-cartesian-axis-line {
      stroke: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .recharts-default-tooltip {
      background-color: var(--dark-panel-surface) !important;
      border-color: var(--dark-panel-border) !important;
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .recharts-tooltip-label,
    body[data-theme='dark'] .recharts-tooltip-item {
      color: var(--dark-text-primary) !important;
    }

    body[data-theme='dark'] .utr-section {
      background-color: var(--dark-panel-surface) !important;
      border: 1px solid var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .utr-title {
      color: var(--dark-text-primary) !important;
    }

    /* global scrollbar tint in dark mode */
    body[data-theme='dark'] * {
      scrollbar-color: var(--scrollBarBgColor) var(--dark-panel-bg);
    }

    /* opt-in attribute: add data-runtime-dark="1" to any container for instant dark surface */
    body[data-theme='dark'] [data-runtime-dark='1'],
    body[data-theme='dark'] [data-runtime-dark='surface'] {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* page/modal containers with hardcoded white backgrounds */
    body[data-theme='dark'] .modalmainpage,
    body[data-theme='dark'] .modalmainpage_approvalQueueList,
    body[data-theme='dark'] .modalmainpage.bellNotification,
    body[data-theme='dark'] .ApprovalContent,
    body[data-theme='dark'] .bulk-reject-dialog,
    body[data-theme='dark'] .template-reject-area,
    body[data-theme='dark'] .homepagecard,
    body[data-theme='dark'] .myWorkflowManagersAll .modalmainpage,
    body[data-theme='dark'] .validationPopupWindow .validateSessionPopup,
    body[data-theme='dark'] .tolAddressPopup,
    body[data-theme='dark'] .bailiffPopup,
    body[data-theme='dark'] .bailiffPopupWindow :where(.bailiffSavePopup),
    body[data-theme='dark'] .cancellationReasonPage .cancellationReasonPopupWindow :where(.cancellationReasonSave),
    body[data-theme='dark'] .pounds-details-content.templateContent,
    body[data-theme='dark'] .cancelReasonPopupWindow .cancelReasonPopup,
    body[data-theme='dark'] .holdreason-content .holdReasonPopupWindow :where(.holdReasonSave),
    body[data-theme='dark'] .purgePopup,
    body[data-theme='dark'] .rejectReasonPage .rejectReasonPopupWindow :where(.rejectReasonSave),
    body[data-theme='dark'] .tribunalAppealPopupWindow :where(.tribunalAppealSave),
    body[data-theme='dark'] .assignCaseToWorkflowPopup .caseNumberSection,
    body[data-theme='dark'] .confirmationBackGround .confirmationPopup,
    body[data-theme='dark'] .preCaseDetailPage .confirmationBackGround .confirmationPopup,
    body[data-theme='dark'] .briefing-details-parent-page,
    body[data-theme='dark'] .ObligatoryReport,
    body[data-theme='dark'] .templateContent,
    body[data-theme='dark'] .workFlowTree,
    body[data-theme='dark'] .splashDiv,
    body[data-theme='dark'] .dropZoneLayer,
    body[data-theme='dark'] .paymentPopupWindow :where(.refund, .chargeBack, .chequeBounced, .recordPayment),
    body[data-theme='dark'] .paymentPopupWindow :where(.addDiscount),
    body[data-theme='dark'] .paymentPopupWindow .transferPayment,
    body[data-theme='dark'] .paymentPopupWindow :where(.addCost),
    body[data-theme='dark'] .reallocate-attachmentContainer,
    body[data-theme='dark'] .reallocateAttachmentDialog,
    body[data-theme='dark'] .reallocateAttachmentDialog .MuiDialog-paper,
    body[data-theme='dark'] .reallocateAttachmentDialogDataSaveWarning,
    body[data-theme='dark'] .caseSearchSection .search-case-list {
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* side panels */
    body[data-theme='dark'] .debtor-side-panel,
    body[data-theme='dark'] .debtor-side-panel-tabs,
    body[data-theme='dark'] .debtor-side-panel-content,
    body[data-theme='dark'] .monitoringConfigContainer,
    body[data-theme='dark'] .monitoringConfigContainer .actionButtons {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* review/detail tabs */
    body[data-theme='dark'] .review-tab-container,
    body[data-theme='dark'] .review-tab-container .main-content .vrm-details-section {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
    }

    /* notification menu */
    body[data-theme='dark'] .notify_menu .MuiMenu-list,
    body[data-theme='dark'] .notify_menuItem.ReadColor,
    body[data-theme='dark'] .titleBar-root .titleBarUser-avatar .titleBarUser-info,
    body[data-theme='dark'] .messages,
    body[data-theme='dark'] .messages .notifyHeader,
    body[data-theme='dark'] #optionsMenu .MuiList-root,
    body[data-theme='dark'] #paymentOptionsMenu .MuiList-root,
    body[data-theme='dark'] .toolsmenuindpage {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .workflowManagersCommon .workFlowTree{
      border-left: 1px solid var(--dark-panel-border) !important;
    }

    /* input fields */
    body[data-theme='dark'] .userDataSection .roleSetup .autoCompleteLazyLoadctrl .autoCompleteInput,
    body[data-theme='dark'] .userDataSection .roleSetup .autoCompleteLazyLoadctrl .autoCompleteLazyLoadinputDiv,
    body[data-theme='dark'] .autocompletectrl .MuiSelect-select:focus,
    body[data-theme='dark'] .gridPageSize .MuiFormControl-root.dropdown .MuiFilledInput-input,
    body[data-theme='dark'] .gridPageSize .MuiInputBase-root.MuiFilledInput-root.MuiInputBase-formControl.MuiInputBase-marginDense.MuiFilledInput-marginDense,
    body[data-theme='dark'] .monitoringConfigContainer .filterField .autoCompleteLazyLoadctrl .autoCompleteInput,
    body[data-theme='dark'] .monitoringConfigContainer .filterField .autoCompleteLazyLoadctrl .autoCompleteLazyLoadinputDiv,
    body[data-theme='dark'] .monitoringConfigContainer .categoryFieldItem .MuiOutlinedInput-root,
    body[data-theme='dark'] .printAdminPage .autoCompleteLazyLoadctrl .autoCompleteInput,
    body[data-theme='dark'] .printAdminPage .autoCompleteLazyLoadctrl .autoCompleteLazyLoadinputDiv,
    body[data-theme='dark'] .numeric_GridCellFlexBox .textbox .MuiInputBase-root,
    body[data-theme='dark'] .manualDiscountPage .contentSection .labelView,
    body[data-theme='dark'] .paymentTab .content .labelView {
      background-color: var(--dark-input-bg, #262d33) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* search bar dropdowns */
    body[data-theme='dark'] .ui.search > .results,
    body[data-theme='dark'] .ui.category.search > .results .category .results,
    body[data-theme='dark'] .category_name,
    body[data-theme='dark'] .mainDiv {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* date/time picker containers inside popups */
    body[data-theme='dark'] .bailiffPopupWindow :where(.bailiffSavePopup) .contentSection .dateandtimepickers .multidateandtime-container,
    body[data-theme='dark'] .bailiffPopupWindow :where(.bailiffSavePopup) .contentSection .dateandtimepickers .multidateandtime-container .multidateandtimeinput,
    body[data-theme='dark'] .cancellationReasonPage .cancellationReasonPopupWindow :where(.cancellationReasonSave) .contentSection .dateandtimepickers .multidateandtime-container,
    body[data-theme='dark'] .cancellationReasonPage .cancellationReasonPopupWindow :where(.cancellationReasonSave) .contentSection .dateandtimepickers .multidateandtime-container .multidateandtimeinput,
    body[data-theme='dark'] .rejectReasonPage .rejectReasonPopupWindow :where(.rejectReasonSave) .contentSection .dateandtimepickers .multidateandtime-container,
    body[data-theme='dark'] .rejectReasonPage .rejectReasonPopupWindow :where(.rejectReasonSave) .contentSection .dateandtimepickers .multidateandtime-container .multidateandtimeinput,
    body[data-theme='dark'] .tribunalAppealPopupWindow :where(.tribunalAppealSave) .contentSection .dateandtimepickers .multidateandtime-container,
    body[data-theme='dark'] .tribunalAppealPopupWindow :where(.tribunalAppealSave) .contentSection .dateandtimepickers .multidateandtime-container .multidateandtimeinput,
    body[data-theme='dark'] .paymentPopupWindow :where(.recordPayment) .contentSection .dateandtimepickers .multidateandtime-container,
    body[data-theme='dark'] .paymentPopupWindow :where(.recordPayment) .contentSection .dateandtimepickers .multidateandtime-container .multidateandtimeinput, {
      background-color: var(--dark-input-bg, #243040) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    /* officer tracking active tab */
    body[data-theme='dark'] .officerTracking .taskTitleDiv .active {
      background-color: var(--dark-panel-surface) !important;
      color: var(--dark-text-primary) !important;
    }

    /* hold-pcn dialog uses --tooltipFontColor as background, which resolves to text color in dark mode */
    body[data-theme='dark'] #holdPcnDialog .holdPcnDialogContainer {
      background: var(--dark-panel-surface) !important;
    }

    body[data-theme='dark'] .card-item {
      background-color: var(--dark-panel-surface) !important;
      border: 1px solid var(--dark-panel-border) !important;
    }

    /* ag-grid headers */
    body[data-theme='dark'] .ag-header-viewport,
    body[data-theme='dark'] .ag-header-row,
    body[data-theme='dark'] .ag-theme-alpine .ag-header {
      background-color: var(--dark-panel-edge-bg) !important;
      color: var(--dark-text-primary) !important;
      border-color: var(--dark-panel-border) !important;
    }

    body[data-theme='dark'] .toast-root.error .MuiAlert-message,
    body[data-theme='dark'] .toast-root.error .MuiAlert-message span,
    body[data-theme='dark'] .toast-root.error .MuiAlert-icon,
    body[data-theme='dark'] .toast-root.error .MuiAlert-action,
    body[data-theme='dark'] .toast-root.error .MuiAlert-action .MuiIconButton-root,
    body[data-theme='dark'] .toast-root.error .MuiAlert-action .MuiSvgIcon-root,
    body[data-theme='dark'] .toast-root.error .MuiAlert-action .MuiSvgIcon-root path,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-message,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-message span,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-icon,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-icon svg,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-action,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-action .MuiIconButton-root,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-action .MuiSvgIcon-root,
    body[data-theme='dark'] #Mui_Alert .MuiAlert-root.MuiAlert-colorError .MuiAlert-action .MuiSvgIcon-root path {
      color: var(--errorToastColor) !important;
      fill: var(--errorToastColor) !important;
      -webkit-text-fill-color: var(--errorToastColor) !important;
    }

    body[data-theme='dark'] .MuiFormHelperText-root.Mui-error,
    body[data-theme='dark'] .MuiFormHelperText-root.Mui-error *,
    body[data-theme='dark'] .MuiInputLabel-root.Mui-error,
    body[data-theme='dark'] .MuiFormLabel-root.Mui-error,
    body[data-theme='dark'] .error,
    body[data-theme='dark'] .Mui-error {
      color: var(--errorToastColor) !important;
      fill: var(--errorToastColor) !important;
      -webkit-text-fill-color: var(--errorToastColor) !important;
    }

    body[data-theme='dark'] .contract_fields .textbox .MuiFormHelperText-root,
    body[data-theme='dark'] .contract_fields .textbox .MuiFormHelperText-root.MuiFormHelperText-filled,
    body[data-theme='dark'] .contract_fields .textbox .MuiFormHelperText-root.Mui-error {
      color: var(--errorToastColor) !important;
      fill: var(--errorToastColor) !important;
      -webkit-text-fill-color: var(--errorToastColor) !important;
    }
    body[data-theme='dark'] .add-non-working-days-dialog__container .dateandtimepickers{
    margin-bottom: 10px !important;
    }
    body[data-theme='dark'] .pa-vrm-wrap .pa-vrm-input.MuiInputBase-root input, .pa-vrm-wrap .textbox .MuiInputBase-root input {
    padding-left: 15px !important;
    }
    body[data-theme='dark'] .enforcement-settings-group .operational-hours-entry .time-range-container .timeinput{
    padding: 6px 2px;
    }
  `
  document.head.appendChild(style)
}

const patchLightDomElementsForDarkTheme = () => {
  if (typeof document === 'undefined') return
  if (document.body.getAttribute('data-theme') !== THEME_DARK) return

  const candidates = document.body.querySelectorAll(
    `div,section,article,main,aside,header,footer,button,th,td,span,${RUNTIME_DARK_INPUT_MATCHER}`
  )

  const maxPatchCount = 2500
  let patched = 0

  for (let index = 0; index < candidates.length; index += 1) {
    if (patched >= maxPatchCount) break

    const element = candidates[index]

    if (
      element.closest(
        '.MuiBackdrop-root, .MuiTooltip-popper, .MuiSnackbar-root, #LoaderOnIndexJS'
      )
    ) {
      continue
    }

    // Keep avatar backgrounds intact; widget avatars use brand-specific light circles.
    if (element.closest('.MuiAvatar-root')) {
      continue
    }

    // Skip chart/badge colour swatches — inline background is an intentional brand colour.
    if (element.closest('.chartLabelColorContainer')) {
      continue
    }

    // Preserve semantic validation/error styling instead of runtime dark recoloring.
    if (
      element.closest(
        '.error, .Mui-error, .MuiFormHelperText-root, .MuiFormHelperText-root.Mui-error, .validation-icon-error, .outlineError, .toast-root.error, .MuiAlert-root.MuiAlert-outlinedError, .MuiAlert-root.MuiAlert-filledError'
      )
    ) {
      continue
    }

    const style = window.getComputedStyle(element)
    if (style.backgroundImage && style.backgroundImage !== 'none') continue

    const lightBackground = isLightRgbColor(style.backgroundColor)
    if (lightBackground) {
      const runtimeDarkType = element.matches(RUNTIME_DARK_INPUT_MATCHER)
        ? 'input'
        : 'surface'
      if (element.getAttribute('data-runtime-dark') !== runtimeDarkType) {
        element.setAttribute('data-runtime-dark', runtimeDarkType)
      }
      patched += 1
    }

    // Detect dark/black text that would be invisible on the dark background.
    const darkText = isDarkRgbColor(style.color)
    if (
      darkText &&
      !element.matches(
        '.mandatorySymbol, .mandatory-symbol, .required-asterisk, svg, img'
      )
    ) {
      if (element.getAttribute('data-runtime-dark-text') !== '1') {
        element.setAttribute('data-runtime-dark-text', '1')
      }
      patched += 1
    }
  }
}

const scheduleRuntimeDarkPatch = (immediate = false) => {
  if (immediate) {
    patchLightDomElementsForDarkTheme()
  }

  if (runtimeDarkPatchTimer) {
    window.clearTimeout(runtimeDarkPatchTimer)
  }

  runtimeDarkPatchTimer = window.setTimeout(() => {
    runtimeDarkPatchTimer = undefined
    patchLightDomElementsForDarkTheme()
  }, 40)
}

const connectRuntimeDarkObserver = () => {
  if (typeof document === 'undefined') return
  if (runtimeDarkObserver) return

  runtimeDarkObserver = new MutationObserver(() => {
    scheduleRuntimeDarkPatch(true)
  })

  runtimeDarkObserver.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
  })
}

const disconnectRuntimeDarkObserver = () => {
  if (runtimeDarkObserver) {
    runtimeDarkObserver.disconnect()
    runtimeDarkObserver = undefined
  }

  if (runtimeDarkPatchTimer) {
    window.clearTimeout(runtimeDarkPatchTimer)
    runtimeDarkPatchTimer = undefined
  }
}

const clearStaleDarkInlineRootStylesInLightTheme = () => {
  if (typeof document === 'undefined') return

  const staleDarkTextColors = new Set([
    '#eaf1f8',
    'rgb(234, 241, 248)',
    'var(--dark-text-primary)',
  ])
  const staleDarkBackgrounds = new Set([
    '#1f2831',
    'rgb(31, 40, 49)',
    'var(--dark-panel-edge-bg)',
  ])

  const clearIfStale = (element) => {
    if (!element || !element.style) return

    const inlineColor = element.style
      .getPropertyValue('color')
      .trim()
      .toLowerCase()
    const inlineBackground = element.style
      .getPropertyValue('background-color')
      .trim()
      .toLowerCase()

    if (staleDarkTextColors.has(inlineColor)) {
      element.style.removeProperty('color')
    }

    if (staleDarkBackgrounds.has(inlineBackground)) {
      element.style.removeProperty('background-color')
    }
  }

  clearIfStale(document.documentElement)
  clearIfStale(document.body)
}

export const applyTheme = (theme = THEME_LIGHT) => {
  if (typeof document === 'undefined') return

  const resolvedTheme = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT
  const body = document.body
  const bodyStyles = body.style

  body.setAttribute('data-theme', resolvedTheme)
  document.documentElement.setAttribute('data-theme', resolvedTheme)
  try {
    localStorage.setItem('bo-theme', resolvedTheme)
  } catch (e) {}

  // Ensure global placeholder rule exists (runs once, works for both themes).
  ensureGlobalPlaceholderStyle()

  // Default light theme variables.
  bodyStyles.setProperty('--carddatatitlefontSize', '19px')
  bodyStyles.setProperty('--header-FontSize', '28px')
  bodyStyles.setProperty('--sub-header-FontSize', '24px')
  bodyStyles.setProperty('--label-FontSize', '16px')
  bodyStyles.setProperty('--heading-FontSize', '32px')
  bodyStyles.setProperty('--subHeading-FontSize', '21px')
  bodyStyles.setProperty('--caseSubHeading-FontSize', '18px')
  bodyStyles.setProperty('--mainheading-FontSize', '24px')
  bodyStyles.setProperty('--listHeader-FontSize', '12px')
  bodyStyles.setProperty('--selectedSubmenu-fontColor', '#333333')
  bodyStyles.setProperty('--inputadornmenttextheight', '13px')
  bodyStyles.setProperty('--additionalTabSideHead', '18px')
  bodyStyles.setProperty('--pcnHeaderFontSize', '18px')
  bodyStyles.setProperty('--headerFontSize', '18px')
  //button--start
  bodyStyles.setProperty('--buttonIconSize', '12px')
  bodyStyles.setProperty('--buttonFontSize', '15px')
  bodyStyles.setProperty('--buttonFontWeight', '500')
  bodyStyles.setProperty('--buttonIconTextGap', '15px')
  bodyStyles.setProperty('--buttonTopBottomPadding', '11px')
  bodyStyles.setProperty('--buttonRightLeftPadding', '22px')
  bodyStyles.setProperty('--buttonBorderRadius', '4px')
  bodyStyles.setProperty('--userCountBand-bg-color', '#E9F2FF')
  bodyStyles.setProperty('--hyperlinkFont-color', 'blue')
  //button-end
  //fieldAlert start
  bodyStyles.setProperty('--fieldAlertFontSize', '14px')
  //fieldAlert end
  //inputControl - start
  bodyStyles.setProperty('--inputControlTextPadding', '10px')
  bodyStyles.setProperty('--inputCalenderIconSize', '24px')
  bodyStyles.setProperty('--inputDropDownIconSize', '16px')
  bodyStyles.setProperty('--placeHolderColor', '#B6B6B6')
  bodyStyles.setProperty('--inputControlBorderColor', '#B6B6B6')
  bodyStyles.setProperty('--inputControlBorderSize', '1px')
  // need to follow entire base controls
  bodyStyles.setProperty('--inputControlDisabledTextColor', '#898989')
  bodyStyles.setProperty('--inputControlDisabledBgColor', '#F3F3F3')
  bodyStyles.setProperty('--inputbackgroundColor', '#FBFBFB')

  //inputControl - end
  //Dialogue - start
  bodyStyles.setProperty('--dialogTitleFontSize', '16px')
  bodyStyles.setProperty('--dialogTitleFontWeight', '700')
  bodyStyles.setProperty('--dialogContentFontSize', '16px')
  bodyStyles.setProperty('--dialogContentFontWeight', '400')
  bodyStyles.setProperty('--dialogBorderRadius', '10px')
  bodyStyles.setProperty('--dialogIconFontSize', '16px')
  //Dialogue - end
  //toast - start
  bodyStyles.setProperty('--errorToastColor', '#ef5350')
  bodyStyles.setProperty('--successToastColor', '#1F3C88')
  //toast - end
  //switch - start
  bodyStyles.setProperty('--unStyledSwitchColor', '#1F3C88')
  bodyStyles.setProperty('--unStyledSwitchDisabledColor', '#1F3C88')
  //switch - end
  //list - start
  bodyStyles.setProperty('--listHeaderFontWeight', '600')
  bodyStyles.setProperty('--listHeaderFontColor', '#000000')
  bodyStyles.setProperty('--listHeaderFontSize', '16px')
  //list - end
  //contractCard - start
  bodyStyles.setProperty('--contractCardTitleFontSize', '24px')
  bodyStyles.setProperty('--contractCardSubHeaderFontWeight', '350')
  bodyStyles.setProperty('--contractCardSubHeaderFontColor', '#9D9D9D')
  bodyStyles.setProperty('--contractCardTitleFontColor', '#3A3A3A')
  bodyStyles.setProperty('--contractCardCountTitleFontColor', '#808080')
  bodyStyles.setProperty('--contractCardCountTitleFontSize', '19px')
  bodyStyles.setProperty('--contractCardCountFontSize', '24px')
  bodyStyles.setProperty('--contratcCardAvatarSize', '72px')
  bodyStyles.setProperty('--contractCardLogoHeight', '51.55px')
  bodyStyles.setProperty('--contractCardLogoWidth', '36px')
  //contractCard - end
  //caseOverView - start
  bodyStyles.setProperty('--gridTitleFontSize', '20px')
  bodyStyles.setProperty('--gridTitleFontColor', '#535353')
  bodyStyles.setProperty('--gridTitleFontWeight', '600')
  bodyStyles.setProperty('--countTitleFontColor', '#808080')
  bodyStyles.setProperty('--countValueFontColor', '#333333')
  bodyStyles.setProperty('--TaskOverviewBorderColor', '#BCBDBE')
  bodyStyles.setProperty('--countValueFontSize', '32px')
  //caseOverview - end
  //tooltip - start
  bodyStyles.setProperty('--tooltipBackgroundColor', '#838383') //got this color from show&tell #e1dbdb //this color from figma - 838383
  bodyStyles.setProperty('--tooltipFontColor', '#FFFFFF') // changing from #FFFFFF to #000000 for visibility
  bodyStyles.setProperty('--tooltipFontSize', '14px')
  bodyStyles.setProperty('--tooltipBorderRadius', '4px')

  //tooltip - end
  // muiIcon size - start
  bodyStyles.setProperty('--muiIconSize', '12px')
  // muiIcon size - end
  // AccordionTitle - start
  bodyStyles.setProperty('--accordionTitleFontSize', '12px')
  // AccordionTitle - end
  // ActionButton - start
  bodyStyles.setProperty('--ActionButtonTitleFontSize', '15px')
  bodyStyles.setProperty('--ActionButtonIconFontSize', '22px')
  bodyStyles.setProperty('--ActionButtonDescriptionFontSize', '14px')
  // ActionButton - end
  // ActionConfirmation - start
  bodyStyles.setProperty('--ActionConfirmationDataFontSize', '14px')
  bodyStyles.setProperty('--ActionCancellationReasonLabelFontColor', '#5A5A5A')
  // ActionConfirmation - end
  // button - start
  bodyStyles.setProperty('--buttonHeight', '40px')
  // button - end
  //mediaViewer - start
  bodyStyles.setProperty('--attachmentViewerBgColor', '#d3d3d3')
  //mediaViewer - end
  /* */

  bodyStyles.setProperty('--accordionDivHeight', '0px')
  bodyStyles.setProperty('--default-FontName', 'Segoe UI')
  // bodyStyles.setProperty('--default-FontName', `'Open Sans',sans-serif`)
  bodyStyles.setProperty('--default-FontColour', 'black')
  // Keep both spellings in sync; some legacy components use --default-FontColor.
  bodyStyles.setProperty('--default-FontColor', 'black')
  bodyStyles.setProperty('--secondary-FontColour', '#1F3C88')
  bodyStyles.setProperty('--default-FontWeight', '400')
  bodyStyles.setProperty('--default-FontSize', '16px')
  bodyStyles.setProperty('--default-BellbuttonFontSize', '10px')
  bodyStyles.setProperty('--default-BellbuttonHeight', '12px')
  bodyStyles.setProperty('--default-BellbuttonWidth', '12px')
  bodyStyles.setProperty('--default-NotificationContentHeadingFontSize', '18px')
  bodyStyles.setProperty('--default-NotificationDateFontSize', '14px')
  bodyStyles.setProperty(
    '--default-NotificationContentUnReadRowColor',
    '#eef3ff'
  )
  bodyStyles.setProperty(
    '--default-NotificationContentRowBorderColor',
    '#e9e9e9'
  )
  bodyStyles.setProperty('--default-NotificationContentReadColor', '#333333')
  bodyStyles.setProperty('--default-NotificationContentUnReadColor', '#000000')
  bodyStyles.setProperty('--label-FontName', 'Segoe UI')
  bodyStyles.setProperty('--label-FontColour', 'Black')
  bodyStyles.setProperty('--label-FontWeight', '400')
  bodyStyles.setProperty('--button-color', '#1F3C88')
  bodyStyles.setProperty('--button-secondary-color', '#ffffff')
  bodyStyles.setProperty('--button-disabled-color', '#B6B6B6')
  bodyStyles.setProperty('--on-hold-status-color', '#ff6c00')
  bodyStyles.setProperty('--templateHeaderSelectedFontColor', '#1F3C88')
  bodyStyles.setProperty('--button-secondary-border-color', '#1F3C88')
  bodyStyles.setProperty('--brandingPaymentURLBGColor', '#EDEDED')
  bodyStyles.setProperty('--brandingActiveMenuBackgroundColor', '#E6E6E6')
  bodyStyles.setProperty('--gridbgcolor', '#f5f5f5')
  bodyStyles.setProperty('--appcontent-bg-color', '#EEEEEE')
  bodyStyles.setProperty('--appcontent-bg-layer-color', '#F9F9F9')
  bodyStyles.setProperty('--font-letter-space', '0px')
  bodyStyles.setProperty('--text-line-height', '1.25vw')
  bodyStyles.setProperty('--textahrlignment', null)
  bodyStyles.setProperty('--footertextahrlignment', 'center')
  bodyStyles.setProperty('--cursorurl', null)
  bodyStyles.setProperty('--cursorname', null)
  bodyStyles.setProperty('--required-color', 'red')
  bodyStyles.setProperty('--ToolbarTextColour', 'Black')
  bodyStyles.setProperty('--footer-default-fontcolor', '#828282')
  bodyStyles.setProperty('--submenufontcolour', '#838383')
  bodyStyles.setProperty('--controlborder-colour', null)
  bodyStyles.setProperty('--header-FontWeight', '600')
  bodyStyles.setProperty('--heading-FontColour', 'Black')
  bodyStyles.setProperty('--subHeading-FontColour', '#0054a6')
  bodyStyles.setProperty('--listHeader-FontColour', '#798093')
  bodyStyles.setProperty('--ListHeaderText-Colour', 'black')
  bodyStyles.setProperty('--TitleBar-TextColour', 'White')
  bodyStyles.setProperty('--cardfontColour', '#808080')
  bodyStyles.setProperty('--ToolbarBackgroundColour', 'White')
  bodyStyles.setProperty('--navbar-color', '#F8F8FF')
  bodyStyles.setProperty('--footer-color', '#F3F3F6')
  bodyStyles.setProperty('--navbar-group-color', '#F3F3F3')
  bodyStyles.setProperty('--titlebar-color', '#F3F3F6')
  bodyStyles.setProperty('--TitleBar-IconColour', '#1F3C88')
  bodyStyles.setProperty('--sub-header-FontWeight', '600')
  bodyStyles.setProperty('--cardavatarbgcolor', '#F1F5FF')
  bodyStyles.setProperty('--carddatatitlebordercolor', '#ECECEC')
  bodyStyles.setProperty('--disableBgColor', '#D9D9D9')
  bodyStyles.setProperty('--disabledDropdownIconColor', '#7d7d7d')
  bodyStyles.setProperty('--disabledDropdownFieldColor', '#E6E6E6')
  bodyStyles.setProperty('--disabledDropdownTextColor', '#B6B6B6')
  bodyStyles.setProperty('--actionButtonBgColor', '#E9F2FF')
  bodyStyles.setProperty('--searchBarControlBorder-colour', '#B6B6B6')
  bodyStyles.setProperty('--attachmentTextColor', '#545454')
  bodyStyles.setProperty('--confirmationTextColor', '#5a5a5a')
  bodyStyles.setProperty('--paymentStatusSuccess', '#29821a')
  bodyStyles.setProperty('--paymentStatusFailure', '#da3131')
  bodyStyles.setProperty('--paymentStatusRemoved', '#8c8c8c')
  bodyStyles.setProperty('--copyIconMouseDownColor', '#ccc9c9')
  bodyStyles.setProperty('--noRecordFoundFontColor', '#b6b6b6')
  bodyStyles.setProperty('--mandatorySymbol', '#ff0000')
  bodyStyles.setProperty('--greenColour', '#278832')
  bodyStyles.setProperty('--whiteColour', '#ffffff')
  bodyStyles.setProperty('--blackColour', '#000000')
  bodyStyles.setProperty('--Freeflow-flight-grey', '#E6E6E6')
  bodyStyles.setProperty('--Freeflow-Grey', '#838383')
  bodyStyles.setProperty('--text-disabled', '#162B4861')
  bodyStyles.setProperty('--FF-Dark-Grey', '#646464')
  bodyStyles.setProperty('--inputControlBackgroundColor', '#ffffff')
  bodyStyles.setProperty('--control-Radius', '6px')
  bodyStyles.setProperty('--attachmentIcon-Border', '#B4C4EE')
  bodyStyles.setProperty('--line-BorderColor', '#ececec')
  bodyStyles.setProperty('--tab-White', '#eef3ff')
  bodyStyles.setProperty('--readOnlyColor', '#8F9DC3')
  bodyStyles.setProperty('--menuLayoutBoxShadow', '#888888')
  bodyStyles.setProperty('--disableMenuColor', '#bbb9b9')
  bodyStyles.setProperty('--tagsBorderColor', '#CAC5C5')
  bodyStyles.setProperty('--messageModalTitleBar', '#e3e3e3')
  bodyStyles.setProperty('--muicheckedHover', '#0000000a')
  bodyStyles.setProperty('--tagsBGColor', '#cfd8e2')
  bodyStyles.setProperty('--gridCellIconWidth', '24px')
  bodyStyles.setProperty('--gridFileErrorCount', '#7b818e')
  bodyStyles.setProperty('--containerBorderRadius', '8px')
  bodyStyles.setProperty('--remainingIndicateTextColor', '#ababab')
  bodyStyles.setProperty('--createdPcnFontSize', '26px')
  bodyStyles.setProperty('--emailControler', '#cfd8e2')
  bodyStyles.setProperty('--statusInitiated-background', '#e6edff')
  bodyStyles.setProperty('--statusCompleted-background', '#c4edc8')
  bodyStyles.setProperty('--statusCompleted-fontcolor', '#219360')
  bodyStyles.setProperty('--tagspopBgColor', '#999')
  bodyStyles.setProperty('--messagepopupscrollbarBgColor', '#aeaaaa57')
  bodyStyles.setProperty('--messagepopupscrollbarColor', '#ffffff57')
  bodyStyles.setProperty('--messagepopupBorderColor', '#e3e3e3')
  bodyStyles.setProperty('--popupBgColor', '#00000080')
  bodyStyles.setProperty('--disableSwitchTrackBgColor', '#1f3c8880')
  bodyStyles.setProperty('--scrollBarBgColor', '#aeaaaa57')
  bodyStyles.setProperty('--scrollBarBorderColor', '#ffffff57')
  bodyStyles.setProperty('--additionalTabBorderColor', '#bcbcbc')
  bodyStyles.setProperty('--inputLableColor', '#646464')
  bodyStyles.setProperty('--popupBackgroundColor', '#00000080')
  bodyStyles.setProperty('--fpnDisabledColor', '#8E9DC3')
  bodyStyles.setProperty('--popupLabelColor', '#606060')
  bodyStyles.setProperty('--popupStageColor', '#3eb54b')
  bodyStyles.setProperty('--inputBackground', '#e6e6e6')
  bodyStyles.setProperty('--attachmentFiletype-Heading', '350')
  bodyStyles.setProperty('--attachmentFiletype-Heading-Font-Size', '13px')
  bodyStyles.setProperty('--ceoTrackTitleColor', '#262D33')
  bodyStyles.setProperty('--priorityFieldBGColor', '#F3F5FC')
  bodyStyles.setProperty('--conditionBorderColor', '#97AEE7')
  bodyStyles.setProperty('--popupTItleColor', '#243B6A')
  bodyStyles.setProperty('--actionButtonBackground', '#F3F5FC')
  bodyStyles.setProperty('--grayBgColor', '#F2F2F2')
  bodyStyles.setProperty('--blueTextColor', '#0C7FE1')
  bodyStyles.setProperty('--mediumColor', '#FFC107')
  bodyStyles.setProperty('--lowColor', '#1A936F')
  bodyStyles.setProperty('--autocompleteAdornmentIconColor', '#162B488F')
  bodyStyles.setProperty('--hyperlinkTextColor', '#0068FF')
  bodyStyles.setProperty('--jobCancelledBackColor', '#FEEBEE')
  bodyStyles.setProperty('--JobAmountBackColor', '#fff3e0')
  bodyStyles.setProperty('--vehicleLogoBackgroundColor', '#D0DEE9')
  bodyStyles.setProperty('--vehicleLogoColor', '#89A9C2')
  bodyStyles.setProperty('--filterColumnBorder', '#80808038')
  bodyStyles.setProperty('--poundpaymentsuucess', '#2a7f62')
  bodyStyles.setProperty('--poundpaymentsuucessBgcolor', '#f1f8e9')
  bodyStyles.setProperty('--poundpaymentsuucessBgcolorData', '#lightGreen')
  bodyStyles.setProperty('--wfAutoAssignTextBgcolor', '#c7d7ff')
  bodyStyles.setProperty('--wfWidget-border-line', '#e0e0e0')
  bodyStyles.setProperty('--wf-overlay-bg', '#00000040')
  bodyStyles.setProperty('--wf-popup-box-shadow', '0 8px 32px #0000002E')
  bodyStyles.setProperty('--wf-utr-section-bg', '#F9F9F999')
  bodyStyles.setProperty('--widget-complete-color', '#4289ff')
  bodyStyles.setProperty('--widget-calender-date-color', '#0066cc')
  bodyStyles.setProperty('--widget-calender-range-date-color', '#e6f0ff')
  bodyStyles.setProperty('--purgeCaseBadgeBackground', '#FEEEEE')
  bodyStyles.setProperty('--airaBackColor', '#f4f9ff')
  bodyStyles.setProperty('--airaButtonDisableBackColor', '#162b4861')
  bodyStyles.setProperty('--airaButtonDisableColor', '#162b481f')
  bodyStyles.setProperty('--airaTextButtonHoverColor', '#1976D2')
  bodyStyles.setProperty('--dpscomponentBorderColor', '#162b483b')
  bodyStyles.setProperty('--airaRedColor', '#D32F2F')
  bodyStyles.setProperty('--airaBgColor', '#f4f9ff')
  bodyStyles.setProperty('--airaLightBlueColor', '#f0f6ff')
  bodyStyles.setProperty('--airaLabelFontSize', '14px')
  bodyStyles.setProperty('--airaPlaceholderFontSize', '16px')
  bodyStyles.setProperty('--airaSubReasonTextColor', '#344761')
  bodyStyles.setProperty('--airaWarningAlertColor', '#663c00')
  bodyStyles.setProperty('--airaFormBorderColor', '#9cc3ff')
  bodyStyles.setProperty('--airagridHeaderColor', '#252627cc')
  bodyStyles.setProperty('--airaParagraphFontColor', '#242526')
  bodyStyles.setProperty('--airaParagraphNumberColor', '#fff9c4')
  bodyStyles.setProperty('--airaTextColor', '#162B48')
  bodyStyles.setProperty('--airaFormLabelColor', '#162B4899')
  bodyStyles.setProperty('--airaFormPlaceholderColor', '#162B48DE')
  bodyStyles.setProperty('--airaTextLinkColor', '#1da5c8')
  bodyStyles.setProperty('--airahoveBGColor', '#e0ecf8')
  bodyStyles.setProperty('--emailAvailableBackColor', '#eafbea')
  bodyStyles.setProperty('--emailAvailableTextColor', '#2b7f63')
  bodyStyles.setProperty('--emailNotAvailableBackColor', '#ffebeb')
  bodyStyles.setProperty('--emailNotAvailableTextColor', '#d3454b')
  bodyStyles.setProperty('--muiGridFilterButtonBgColor', '#303f9f24')
  bodyStyles.setProperty('--muiGridFilterTextColor', '#303f9f')
  bodyStyles.setProperty('--dialogDescTextColor', '#252627')
  bodyStyles.setProperty('--airaTextBorderColor', '#162b483b')
  bodyStyles.setProperty('--airaSelectionHoverColor', '#bcc2cb1f')
  bodyStyles.setProperty('--airaInActiveBgCOlor', '#fafafa')
  bodyStyles.setProperty('--airaGridChipBgColor', '#FFE082')
  bodyStyles.setProperty('--airaButtonGroupBgColor', '#757575')
  bodyStyles.setProperty('--airaChipBgColor', '#00000014')
  bodyStyles.setProperty('--helperIconBgColor', '#0000008c')
  bodyStyles.setProperty('--docIconColor', '#0D6EFD')
  bodyStyles.setProperty('--warningColor', '#EF6C00')
  bodyStyles.setProperty('--attachmentTitleColor', '#041643')

  bodyStyles.setProperty('--moreVertIconColor', '#0000008a')
  bodyStyles.setProperty('--favIconFileNameColor', '#3276CF')
  bodyStyles.setProperty('--linkedcases-summaryfield', '#0057b8')
  bodyStyles.setProperty('--bluedark-holdtask', '#1F3C88')
  bodyStyles.setProperty('--awaiting-qa-indicator-color', '#FB8C00')
  bodyStyles.setProperty('--hyperlink-file-name', '#336afa')
  bodyStyles.setProperty('--vertical-line-color', '#acacac')
  bodyStyles.setProperty('--stepper-border-color', '#d0d0d0')
  bodyStyles.setProperty('--stepper-active-color', '#0468c8')
  bodyStyles.setProperty('--stepper-completed-color', '#008C76')
  bodyStyles.setProperty('--thumbnail-border-color', '#8699d1')
  bodyStyles.setProperty('--edit-icon-color', '#0078d4')
  bodyStyles.setProperty('--vrm-container-color', '#F9DF1A')
  bodyStyles.setProperty('--docs-black-color', '#222')
  bodyStyles.setProperty('--docs-viewer-header-color', '#1a1a1a')
  bodyStyles.setProperty('--docs-viewer-table-header-color', '#ccc')
  bodyStyles.setProperty('--docs-loading-content-color', '#666')
  bodyStyles.setProperty('--abandonColor', '#E53935')
  bodyStyles.setProperty('--priority-yes-color', '#1e7e34')
  bodyStyles.setProperty('--priority-yes-bg', '#e6f4ea')
  bodyStyles.setProperty('--priority-no-color', '#e65100')
  bodyStyles.setProperty('--priority-no-bg', '#fff3e0')
  // Enforcement Map - start
  bodyStyles.setProperty('--em-success-bg', '#e8f5e9')
  bodyStyles.setProperty('--em-success-color', '#2e7d32')
  bodyStyles.setProperty('--em-error-bg', '#feebee')
  bodyStyles.setProperty('--em-error-color', '#c62828')
  bodyStyles.setProperty('--em-info-bg', '#e3f2fd')
  bodyStyles.setProperty('--em-info-color', '#1565c0')
  bodyStyles.setProperty('--em-info-border', '#90caf9')
  bodyStyles.setProperty('--em-warning-bg', '#fff3e0')
  bodyStyles.setProperty('--em-warning-border', '#ffcc80')
  bodyStyles.setProperty('--em-warning-color', '#e65100')
  bodyStyles.setProperty('--em-neutral-bg', '#f5f5f5')
  bodyStyles.setProperty('--em-neutral-color', '#616161')
  bodyStyles.setProperty('--em-chip-bg', '#a2c1ea')
  bodyStyles.setProperty('--em-chip-color', '#275798')
  bodyStyles.setProperty('--em-obs-border', '#43a047')
  bodyStyles.setProperty('--em-pcn-border', '#e53935')
  bodyStyles.setProperty('--em-badge-medium', '#FF9800')
  bodyStyles.setProperty('--em-row-border', '#eeeeee')
  // Enforcement Map - end
  // Integration File Transfer - start
  bodyStyles.setProperty('--ift-card-border', '#eaeef3')
  bodyStyles.setProperty('--ift-primary-icon-bg', 'rgba(50, 118, 207, 0.1)')
  bodyStyles.setProperty('--ift-success-icon-bg', 'rgba(34, 160, 107, 0.12)')
  bodyStyles.setProperty('--ift-success-color', '#1f8a5c')
  bodyStyles.setProperty('--ift-warning-icon-bg', 'rgba(242, 169, 59, 0.15)')
  bodyStyles.setProperty('--ift-warning-color', '#b97a12')
  bodyStyles.setProperty('--ift-info-icon-bg', 'rgba(50, 118, 207, 0.12)')
  bodyStyles.setProperty('--ift-danger-icon-bg', 'rgba(225, 75, 75, 0.12)')
  bodyStyles.setProperty('--ift-danger-color', '#c22a2a')
  bodyStyles.setProperty('--ift-label-color', '#5b6573')
  bodyStyles.setProperty('--ift-value-color', '#212121')
  // Integration File Transfer - end
  bodyStyles.setProperty('--fault-status-open-color', '#2196f3')
  bodyStyles.setProperty('--fault-status-inprogress-color', '#ff9800')
  bodyStyles.setProperty('--fault-status-closed-color', '#4caf50')
  bodyStyles.setProperty('--fault-status-rejected-color', '#f44336')
  bodyStyles.setProperty('--fault-status-default-color', '#9e9e9e')
  bodyStyles.setProperty('--sub-heading-text-color', '#5a5a5a')

  // Dark theme token map.
  bodyStyles.setProperty('--dark-panel-bg', '#262d33')
  bodyStyles.setProperty('--dark-panel-surface', '#262d33')
  bodyStyles.setProperty('--dark-panel-surface-elevated', '#2a333d')
  bodyStyles.setProperty('--dark-panel-hover', '#374351')
  bodyStyles.setProperty('--dark-panel-border', '#4f5d6d')
  bodyStyles.setProperty('--dark-panel-shadow', '#151d26')
  bodyStyles.setProperty('--dark-panel-edge-bg', '#1f2831')
  bodyStyles.setProperty('--dark-topbar-bg', '#1f2a36')
  bodyStyles.setProperty('--dark-shell-border', '#7d8ea2')
  bodyStyles.setProperty('--dark-search-bg', '#262d33')
  bodyStyles.setProperty('--dark-search-border', '#838383')
  bodyStyles.setProperty('--dark-search-text', '#eaf1f8')
  bodyStyles.setProperty('--dark-search-placeholder', '#B6B6B6')
  bodyStyles.setProperty('--dark-search-action-bg', '#3f78c6')
  bodyStyles.setProperty('--dark-active-menu-bg', '#3f78c6')
  bodyStyles.setProperty('--dark-active-menu-bg-hover', '#4b86d6')
  bodyStyles.setProperty('--dark-text-primary', '#eaf1f8')
  bodyStyles.setProperty('--dark-text-secondary', '#c3cfdb')
  bodyStyles.setProperty('--dark-text-tertiary', '#a7b7c8')
  bodyStyles.setProperty('--dark-avatar-bg', '#3f78c6')
  bodyStyles.setProperty('--dark-notification-bg', '#f2c94c')
  bodyStyles.setProperty('--dark-notification-text', '#1a1f27')
  bodyStyles.setProperty('--dark-panels-border', '#262d33')

  if (resolvedTheme === THEME_DARK) {
    bodyStyles.setProperty('--default-FontColour', 'var(--dark-text-primary)')
    bodyStyles.setProperty(
      '--selectedSubmenu-fontColor',
      'var(--dark-search-action-bg)'
    )
    bodyStyles.setProperty('--label-FontColour', 'var(--dark-text-secondary)')
    bodyStyles.setProperty('--appcontent-bg-color', 'var(--dark-panel-edge-bg)')
    bodyStyles.setProperty(
      '--appcontent-bg-layer-color',
      'var(--dark-panel-bg)'
    )
    bodyStyles.setProperty('--whiteColour', 'var(--dark-panel-surface)')
    bodyStyles.setProperty('--gridbgcolor', 'var(--dark-panel-surface)')
    bodyStyles.setProperty('--ToolbarBackgroundColour', 'var(--dark-topbar-bg)')
    bodyStyles.setProperty('--navbar-color', 'var(--dark-panel-bg)')
    bodyStyles.setProperty('--navbar-group-color', 'var(--dark-panel-bg)')
    bodyStyles.setProperty('--titlebar-color', 'var(--dark-topbar-bg)')
    bodyStyles.setProperty(
      '--ListHeaderText-Colour',
      'var(--dark-text-primary)'
    )
    bodyStyles.setProperty('--listHeaderFontColor', 'var(--dark-text-primary)')
    bodyStyles.setProperty('--submenufontcolour', 'var(--dark-text-secondary)')
    bodyStyles.setProperty('--cardfontColour', 'var(--dark-text-secondary)')
    bodyStyles.setProperty(
      '--contractCardTitleFontColor',
      'var(--dark-text-primary)'
    )
    bodyStyles.setProperty(
      '--contractCardSubHeaderFontColor',
      'var(--dark-text-secondary)'
    )
    bodyStyles.setProperty(
      '--contractCardCountTitleFontColor',
      'var(--dark-text-secondary)'
    )
    bodyStyles.setProperty('--gridTitleFontColor', 'var(--dark-text-secondary)')
    bodyStyles.setProperty(
      '--countTitleFontColor',
      'var(--dark-text-secondary)'
    )
    bodyStyles.setProperty('--countValueFontColor', 'var(--dark-text-primary)')
    bodyStyles.setProperty(
      '--inputControlBorderColor',
      'var(--dark-search-border)'
    )
    bodyStyles.setProperty(
      '--searchBarControlBorder-colour',
      'var(--dark-search-border)'
    )
    bodyStyles.setProperty('--inputbackgroundColor', 'var(--dark-search-bg)')
    bodyStyles.setProperty(
      '--inputControlBackgroundColor',
      'var(--dark-search-bg)'
    )
    bodyStyles.setProperty('--scrollBarBgColor', '#262B34')
    bodyStyles.setProperty('--scrollBarBorderColor', '#2a333d')
    bodyStyles.setProperty('--messagepopupscrollbarBgColor', '#5f7186')
    bodyStyles.setProperty('--messagepopupscrollbarColor', '#2a333d')
    bodyStyles.setProperty(
      '--inputControlDisabledBgColor',
      'var(--dark-panel-surface-elevated)'
    )
    bodyStyles.setProperty(
      '--inputControlDisabledTextColor',
      'var(--dark-text-tertiary)'
    )
    bodyStyles.setProperty(
      '--disableBgColor',
      'var(--dark-panel-surface-elevated)'
    )
    bodyStyles.setProperty(
      '--disabledDropdownFieldColor',
      'var(--dark-panel-surface-elevated)'
    )
    bodyStyles.setProperty(
      '--disabledDropdownTextColor',
      'var(--dark-text-tertiary)'
    )
    bodyStyles.setProperty(
      '--placeHolderColor',
      'var(--dark-search-placeholder)'
    )
    bodyStyles.setProperty('--button-color', 'var(--dark-search-action-bg)')
    bodyStyles.setProperty('--tab-White', '#262d33')
    bodyStyles.setProperty(
      '--button-secondary-border-color',
      'var(--dark-shell-border)'
    )
    bodyStyles.setProperty(
      '--button-disabled-color',
      'var(--dark-panel-border)'
    )
    bodyStyles.setProperty('--actionButtonBgColor', 'var(--dark-panel-surface)')
    bodyStyles.setProperty('--cardavatarbgcolor', '#F1F5FF')
    bodyStyles.setProperty(
      '--carddatatitlebordercolor',
      'var(--dark-panel-border)'
    )
    bodyStyles.setProperty('--line-BorderColor', 'var(--dark-panel-border)')
    bodyStyles.setProperty('--menuLayoutBoxShadow', '#888888')
    bodyStyles.setProperty('--TitleBar-IconColour', 'var(--dark-text-primary)')
    bodyStyles.setProperty(
      '--footer-default-fontcolor',
      'var(--dark-text-secondary)'
    )
    bodyStyles.setProperty(
      '--default-NotificationContentReadColor',
      'var(--dark-text-primary)'
    )
    bodyStyles.setProperty(
      '--default-NotificationContentUnReadColor',
      'var(--dark-text-primary)'
    )
    bodyStyles.setProperty(
      '--default-NotificationContentUnReadRowColor',
      'var(--dark-panel-surface-elevated)'
    )
    bodyStyles.setProperty(
      '--default-NotificationContentRowBorderColor',
      'var(--dark-panel-border)'
    )
    bodyStyles.setProperty(
      '--tooltipBackgroundColor',
      'var(--dark-panel-surface-elevated)'
    )
    bodyStyles.setProperty('--tooltipFontColor', 'var(--dark-text-primary)')
    bodyStyles.setProperty('--blackColour', 'var(--dark-text-primary)')
    bodyStyles.setProperty('--default-FontColor', 'var(--dark-text-primary)')
    bodyStyles.setProperty('--scrollBarBgColor', '#3a4656')
    bodyStyles.setProperty('--scrollBarBorderColor', '#1f2831')

    // AIRA-specific dark palette overrides.
    bodyStyles.setProperty('--airaBackColor', '#1f2831')
    bodyStyles.setProperty('--airaBgColor', '#1f2831')
    bodyStyles.setProperty('--airaLightBlueColor', '#2a333d')
    bodyStyles.setProperty('--airaSubReasonTextColor', '#c3cfdb')
    bodyStyles.setProperty('--airaWarningAlertColor', '#ffc66d')
    bodyStyles.setProperty('--airaFormBorderColor', '#3f78c6')
    bodyStyles.setProperty('--airagridHeaderColor', '#eaf1f8')
    bodyStyles.setProperty('--dataGridSurfaceColor', '#262d33')
    bodyStyles.setProperty('--dataGridTextColor', 'var(--dark-text-primary)')
    bodyStyles.setProperty('--dataGridBorderColor', 'var(--dark-panel-border)')
    bodyStyles.setProperty('--dataGridSelectBgColor', 'var(--dark-search-bg)')
    bodyStyles.setProperty(
      '--dataGridSelectTextColor',
      'var(--dark-text-primary)'
    )
    bodyStyles.setProperty(
      '--dataGridSelectBorderColor',
      'var(--dark-search-border)'
    )
    bodyStyles.setProperty('--airaParagraphFontColor', '#d4deea')
    bodyStyles.setProperty('--airaParagraphNumberColor', '#3b3a1f')
    bodyStyles.setProperty('--airaTextColor', '#eaf1f8')
    bodyStyles.setProperty('--airaFormLabelColor', '#b5c3d2')
    bodyStyles.setProperty('--airaFormPlaceholderColor', '#93a6ba')
    bodyStyles.setProperty('--airaTextLinkColor', '#69b2ff')
    bodyStyles.setProperty('--airahoveBGColor', '#314152')
    bodyStyles.setProperty('--airaButtonDisableBackColor', '#5a6880')
    bodyStyles.setProperty('--airaButtonDisableColor', '#3d4a60')
    bodyStyles.setProperty('--airaTextBorderColor', '#4f5d6d')
    bodyStyles.setProperty('--airaSelectionHoverColor', '#3a4757')
    bodyStyles.setProperty('--airaInActiveBgCOlor', '#2a333d')
    bodyStyles.setProperty('--airaGridChipBgColor', '#665735')
    bodyStyles.setProperty('--airaButtonGroupBgColor', '#4b5a6d')
    bodyStyles.setProperty('--airaChipBgColor', '#3a4757')

    ensureRuntimeDarkStyle()
    connectRuntimeDarkObserver()
    scheduleRuntimeDarkPatch(true)
  } else {
    disconnectRuntimeDarkObserver()

    // Some flows can leave stale dark inline styles on html/body after a mode toggle.
    // Clear only known dark values so we do not affect unrelated runtime styling.
    clearStaleDarkInlineRootStylesInLightTheme()

    // Clear runtime dark markers so light theme is fully restored.
    const runtimePatchedElements = document.body.querySelectorAll(
      '[data-runtime-dark], [data-runtime-dark-text]'
    )
    for (let i = 0; i < runtimePatchedElements.length; i += 1) {
      runtimePatchedElements[i].removeAttribute('data-runtime-dark')
      runtimePatchedElements[i].removeAttribute('data-runtime-dark-text')
    }
  }
}

export const initializeTheme = () => {
  let themeToUse
  try {
    const cached = localStorage.getItem('bo-theme')
    if (cached === THEME_DARK || cached === THEME_LIGHT) {
      themeToUse = cached
    }
  } catch (e) {}

  if (!themeToUse) {
    const prefersDark =
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    themeToUse = prefersDark ? THEME_DARK : THEME_LIGHT
  }

  applyTheme(themeToUse)

  window.setAppTheme = (theme) => applyTheme(theme)

  return themeToUse
}
